import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePerplexityAgents, formatAgentResponse } from './hooks/usePerplexityAgents';
import AgentStatusIndicator from './components/AgentStatusIndicator';
import AgentDetailsModal from './components/AgentDetailsModal';
import AgentResponse from './components/AgentResponse';
import { 
  Heart, 
  Send, 
  Stethoscope, 
  Activity, 
  User,
  Bot,
  Pill,
  Thermometer,
  Scale,
  Phone,
  MessageSquare,
  Calendar,
  Camera,
  AlertTriangle,
  CheckCircle,
  Bell,
  Users,
  Upload,
  Video,
  Zap,
  Plus,
  Minus,
  Check,
  X,
  Smile,
  Mic,
  Image,
  Paperclip,
  TrendingUp,
  Shield,
  Brain,
  Bluetooth,
  Wifi,
  Star,
  Award,
  Target,
  Eye,
  Volume2,
  MicIcon,
  Headphones,
  Clock,
  Droplet,
  Trash2,
  ChevronDown,
  Trophy,
  Flame,
  Star as StarIcon,
  Award as AwardIcon,
  Target as TargetIcon,
  Eye as EyeIcon,
  Volume2 as Volume2Icon,
  MicIcon as MicIconIcon,
  Headphones as HeadphonesIcon,
  Clock as ClockIcon,
  Droplet as DropletIcon,
  Trash2 as Trash2Icon,
  ChevronDown as ChevronDownIcon
} from 'lucide-react';

const ElderlyNurseAgent = () => {
  // Perplexity Agents Integration
  const { 
    processMessage, 
    isLoading: agentLoading, 
    currentAgent, 
    error: agentError,
    classifyIntent,
    getAgentStats,
    getAvailableAgents
  } = usePerplexityAgents();

  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentReminder, setCurrentReminder] = useState(null);
  const [showFoodAnalyzer, setShowFoodAnalyzer] = useState(false);
  const [showVitalsInput, setShowVitalsInput] = useState(false);
  const [showMoodTracker, setShowMoodTracker] = useState(false);
  const [celebrateAction, setCelebrateAction] = useState(null);
  const [pulseNotification, setPulseNotification] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [activeNotification, setActiveNotification] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Agent status display
  const [showAgentInfo, setShowAgentInfo] = useState(false);
  const [showAgentDetails, setShowAgentDetails] = useState(false);
  
  // Tracking de notificaciones previas
  const [notificationHistory] = useState({
    hydration: [],
    activity: [],
    medication: [],
    mood: [],
    urgent: []
  });

  const MAX_HISTORY = 5; // Mantener track de los últimos 5 mensajes
  const [showAttachmentPreview, setShowAttachmentPreview] = useState(false);
  const [attachmentPreview, setAttachmentPreview] = useState({
    type: null, // 'image' or 'file'
    file: null,
    previewUrl: null,
    message: ''
  });
  const [emergencyCall, setEmergencyCall] = useState({
    calling: false,
    sendingData: false,
    completed: false,
    hasBeenTriggered: false,
    countdown: 0,
    isTestMode: true // Indica que estamos en modo de prueba
  });
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [showBottomPanel, setShowBottomPanel] = useState(null); // 'medication', 'vitals', 'doctor', 'water'
  const [animationsCompleted, setAnimationsCompleted] = useState(false); // New state to control initial animations
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [currentChatId, setCurrentChatId] = useState('current');
  const messagesEndRef = useRef(null);

  // NEW: Advanced Features State
  const [voiceRecognition, setVoiceRecognition] = useState({
    isListening: false,
    isSupported: 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window,
    transcript: '',
    confidence: 0
  });
  
  const [healthAnalytics, setHealthAnalytics] = useState({
    riskScore: Math.round(15), // Low risk: 0-30, Medium: 31-70, High: 71-100
    trends: {
      bloodPressure: 'stable',
      heartRate: 'improving',
      medication: 'excellent',
      mood: 'positive'
    },
    predictions: {
      nextMedicationTime: '2:00 PM',
      riskFactors: ['dehydration', 'missed_evening_medication'],
      recommendations: ['Drink more water', 'Set evening alarm']
    }
  });

  const [familyDashboard, setFamilyDashboard] = useState({
    connectedFamily: [
      { 
        name: 'Maria', 
        relationship: 'Daughter',
        role: 'Primary Caregiver',
        status: 'online', 
        lastSeen: 'now', 
        notifications: true,
        phone: '+1 (555) 123-4567',
        location: 'Home (2.3 miles away)'
      },
      { 
        name: 'Carlos', 
        relationship: 'Son',
        role: 'Emergency Contact',
        status: 'offline', 
        lastSeen: '2h ago', 
        notifications: true,
        phone: '+1 (555) 987-1234',
        location: 'Work (15 miles away)'
      }
    ],
    sharedData: {
      vitals: true,
      medications: true,
      mood: true,
      location: false
    },
    emergencyContacts: 3
  });

  const [gamification, setGamification] = useState({
    points: 850,
    level: 7,
    todayPoints: 45,
    streaks: {
      medication: 12,
      hydration: 5,
      vitals: 3
    },
    achievements: [
      { id: 1, name: 'Medication Master', icon: '💊', earned: true, date: '2024-01-10' },
      { id: 2, name: 'Hydration Hero', icon: '💧', earned: true, date: '2024-01-08' },
      { id: 3, name: 'Health Champion', icon: '🏆', earned: false, progress: 80 }
    ],
    weeklyChallenge: {
      name: 'Perfect Week',
      description: 'Take all medications for 7 days',
      progress: 5,
      target: 7,
      reward: 100
    }
  });

  const [smartFeatures, setSmartFeatures] = useState({
    deviceConnections: {
      glucometer: { connected: true, lastReading: '95 mg/dL', time: '8:30 AM' },
      bloodPressure: { connected: true, lastReading: '120/80', time: '8:15 AM' },
      smartwatch: { connected: false, lastReading: null, time: null },
      scale: { connected: true, lastReading: '165 lbs', time: '7:45 AM' }
    },
    aiInsights: [
      { type: 'positive', message: 'Blood pressure trending down this week! 📉', priority: 'low' },
      { type: 'warning', message: 'Glucose slightly elevated after lunch. Consider lighter meals.', priority: 'medium' },
      { type: 'achievement', message: 'Congratulations! 12-day medication streak! 🎉', priority: 'high' }
    ],
    voiceCommands: {
      enabled: true,
      language: 'en-US',
      commands: [
        'Sophira, did I take my morning medication?',
        'Sophira, how is my blood pressure?',
        'Sophira, call my daughter',
        'Sophira, I don\'t feel well',
        'Sophira, record my mood as good'
      ]
    }
  });

  // Enhanced patient data with more interactive features
  const [patientData, setPatientData] = useState({
    name: "Robert",
    age: 72,
    conditions: ["Type 2 Diabetes", "Hypertension", "High Cholesterol"],
    medications: [
      { 
        id: 1,
        name: "Metformin", 
        color: "white", 
        time: "8:00 AM", 
        remaining: 3, 
        dosage: "500mg",
        taken: false,
        lastTaken: null
      },
      { 
        id: 2,
        name: "Lisinopril", 
        color: "pink", 
        time: "8:00 AM", 
        remaining: 15, 
        dosage: "10mg",
        taken: false,
        lastTaken: null
      },
      { 
        id: 3,
        name: "Atorvastatin", 
        color: "blue", 
        time: "8:00 PM", 
        remaining: 8, 
        dosage: "20mg",
        taken: false,
        lastTaken: null
      }
    ],
    vitals: {
      temperature: "98.6°F",
      heartRate: "72 bpm",
      weight: "165 lbs",
      bloodPressure: "120/80",
      lastUpdated: new Date()
    },
    mood: {
      current: "good",
      history: []
    },
    hydration: {
      goal: 8,
      current: 2,
      lastDrink: new Date()
    },
    emergencyContact: {
      name: "Maria Rodriguez",
      relationship: "Daughter",
      phone: "+1 (555) 123-4567",
      role: "Primary Caregiver"
    },
    doctor: {
      name: "Dr. Martinez",
      specialty: "Primary Care",
      phone: "+1 (555) 987-6543",
      nextAppointment: "2024-01-15"
    }
  });

  // Chat History State with example conversations
  const [chatHistory, setChatHistory] = useState({
    'current': [
      {
        id: 1,
        text: "Hello Robert! I'm Sophira, your AI health companion. I'm here to help you manage your medications, track your health, and stay connected with your family. How are you feeling today?",
        sender: 'nurse',
        timestamp: new Date(Date.now() - 3600000), // 1 hour ago
      }
    ],
    'medication-chat': [
      {
        id: 1,
        text: "I want to take my medication",
        sender: 'user',
        timestamp: new Date(Date.now() - 86400000), // 1 day ago
      },
      {
        id: 2,
        text: "Perfect! Let me help you with your medications. You have 3 medications scheduled for this morning: Metformin (white, 500mg), Lisinopril (pink, 10mg), and Atorvastatin (blue, 20mg). Which one would you like to take?",
        sender: 'nurse',
        timestamp: new Date(Date.now() - 86400000 + 30000),
      },
      {
        id: 3,
        text: "I took the Metformin",
        sender: 'user',
        timestamp: new Date(Date.now() - 86400000 + 120000),
      },
      {
        id: 4,
        text: "🎉 Excellent! I've marked your Metformin as taken. You're doing great with your medication routine! This helps keep your blood sugar stable. Don't forget your other morning medications when you're ready.",
        sender: 'nurse',
        timestamp: new Date(Date.now() - 86400000 + 150000),
        type: 'celebration'
      }
    ],
    'vitals-chat': [
      {
        id: 1,
        text: "I want to record my vital signs",
        sender: 'user',
        timestamp: new Date(Date.now() - 172800000), // 2 days ago
      },
      {
        id: 2,
        text: "Great idea! Regular vital sign monitoring is crucial for managing your diabetes and hypertension. I'll help you record your blood pressure, heart rate, weight, and blood glucose. Let's start with what you'd like to measure.",
        sender: 'nurse',
        timestamp: new Date(Date.now() - 172800000 + 30000),
      },
      {
        id: 3,
        text: "My blood pressure is 125/82",
        sender: 'user',
        timestamp: new Date(Date.now() - 172800000 + 90000),
      },
      {
        id: 4,
        text: "📊 Thank you! I've recorded your blood pressure as 125/82. This is slightly elevated but within acceptable range for your condition. I've notified Dr. Martinez and added this to your health trends. Keep monitoring and remember to take your Lisinopril as prescribed.",
        sender: 'nurse',
        timestamp: new Date(Date.now() - 172800000 + 120000),
        type: 'vitals-update'
      }
    ],
    'voice-chat': [
      {
        id: 1,
        text: "Sophira, how is my blood pressure?",
        sender: 'user',
        timestamp: new Date(Date.now() - 259200000), // 3 days ago
        type: 'voice-command'
      },
      {
        id: 2,
        text: "🎤 Voice command received! Your current blood pressure reading is 120/80, which was recorded this morning. This is excellent - it's in the normal range! Your blood pressure has been stable this week, which is great news for your cardiovascular health.",
        sender: 'nurse',
        timestamp: new Date(Date.now() - 259200000 + 30000),
        type: 'voice-response'
      },
      {
        id: 3,
        text: "Sophira, call my daughter",
        sender: 'user',
        timestamp: new Date(Date.now() - 259200000 + 300000),
        type: 'voice-command'
      },
      {
        id: 4,
        text: "📞 I understand you'd like to contact Maria (your daughter). While I can't make calls directly, I can help you in other ways: I can send her an update about your health status, or you can use the emergency button if this is urgent. Maria is currently online and was last active now. Would you like me to send her a health update?",
        sender: 'nurse',
        timestamp: new Date(Date.now() - 259200000 + 330000),
        type: 'voice-response'
      }
    ],
    'food-analysis-chat': [
      {
        id: 1,
        text: "I want to analyze my food",
        sender: 'user',
        timestamp: new Date(Date.now() - 345600000), // 4 days ago
      },
      {
        id: 2,
        text: "🍎 Great choice! Food analysis helps manage your diabetes and overall health. You can take a photo of your meal or describe what you're eating. I'll analyze the nutritional content and provide recommendations based on your conditions.",
        sender: 'nurse',
        timestamp: new Date(Date.now() - 345600000 + 30000),
      },
      {
        id: 3,
        text: "I had grilled chicken with vegetables and brown rice",
        sender: 'user',
        timestamp: new Date(Date.now() - 345600000 + 120000),
      },
      {
        id: 4,
        text: "🥗 Excellent meal choice, Robert! This is perfect for managing your Type 2 diabetes:\n\n✅ Grilled chicken: High protein, low carbs\n✅ Vegetables: Rich in fiber and nutrients\n✅ Brown rice: Complex carbs, better than white rice\n\nEstimated carbs: ~45g. This should have minimal impact on your blood sugar. Great job choosing a balanced, diabetes-friendly meal!",
        sender: 'nurse',
        timestamp: new Date(Date.now() - 345600000 + 150000),
        type: 'food-analysis'
      }
    ],
    'mood-chat': [
      {
        id: 1,
        text: "How I feel",
        sender: 'user',
        timestamp: new Date(Date.now() - 432000000), // 5 days ago
      },
      {
        id: 2,
        text: "😊 I'd love to know how you're feeling today! Your emotional well-being is just as important as your physical health. Please select your current mood from the options that just appeared.",
        sender: 'nurse',
        timestamp: new Date(Date.now() - 432000000 + 30000),
      },
      {
        id: 3,
        text: "I'm feeling good today",
        sender: 'user',
        timestamp: new Date(Date.now() - 432000000 + 90000),
      },
      {
        id: 4,
        text: "🌟 That's wonderful to hear, Robert! I'm so glad you're feeling good today. Positive mood contributes significantly to your overall health and can even help with blood pressure and diabetes management. I've recorded your mood as 'good' in your health analytics. Keep up the positive attitude!",
        sender: 'nurse',
        timestamp: new Date(Date.now() - 432000000 + 120000),
        type: 'mood-response'
      }
    ],
    'emergency-chat': [
      {
        id: 1,
        text: "Call doctor",
        sender: 'user',
        timestamp: new Date(Date.now() - 518400000), // 6 days ago
      },
      {
        id: 2,
        text: "🚨 I understand you need to contact your doctor. This seems important! I'm preparing to help you reach Dr. Martinez (Primary Care) at +1 (555) 987-6543. I can also alert your emergency contact Maria if needed. Are you experiencing any symptoms I should know about?",
        sender: 'nurse',
        timestamp: new Date(Date.now() - 518400000 + 30000),
        type: 'emergency-response'
      },
      {
        id: 3,
        text: "I'm feeling dizzy",
        sender: 'user',
        timestamp: new Date(Date.now() - 518400000 + 90000),
      },
      {
        id: 4,
        text: "⚠️ Dizziness can be serious, especially with your conditions. I'm immediately notifying:\n\n📞 Dr. Martinez: +1 (555) 987-6543\n👨‍⚕️ Emergency Contact: Maria (Daughter)\n📊 Sending your recent vitals and medication history\n\nPlease sit down safely. If symptoms worsen, call 911. Your family has been notified of this situation.",
        sender: 'nurse',
        timestamp: new Date(Date.now() - 518400000 + 120000),
        type: 'emergency-alert'
      }
    ]
  });

  const [chatTitles, setChatTitles] = useState({
    'current': 'Current Conversation',
    'medication-chat': 'Medication Management',
    'vitals-chat': 'Vital Signs Recording',
    'voice-chat': 'Voice Commands',
    'food-analysis-chat': 'Food Analysis',
    'mood-chat': 'Mood Tracking',
    'emergency-chat': 'Emergency Contact'
  });

  // Current messages based on selected chat - Initialize with current chat messages
  const [messages, setMessages] = useState(() => {
    return chatHistory['current'] || [];
  });

  // Sync messages with current chat when currentChatId changes
  useEffect(() => {
    setMessages(chatHistory[currentChatId] || []);
  }, [currentChatId, chatHistory]);

  // Functions for chat history management
  const switchToChat = (chatId) => {
    setCurrentChatId(chatId);
    setMessages(chatHistory[chatId] || []);
    setShowChatHistory(false);
  };

  const createNewChat = () => {
    const newChatId = `chat_${Date.now()}`;
    const welcomeMessage = {
      id: Date.now(),
      text: "Hello! I'm Sophira, your AI health companion. How can I help you today?",
      sender: 'nurse',
      timestamp: new Date()
    };
    
    setChatHistory(prev => ({
      ...prev,
      [newChatId]: [welcomeMessage]
    }));
    
    setChatTitles(prev => ({
      ...prev,
      [newChatId]: "New Conversation"
    }));
    
    setCurrentChatId(newChatId);
    setMessages([welcomeMessage]);
    setShowChatHistory(false);
  };

  const updateCurrentChat = (newMessages) => {
    setMessages(newMessages);
    setChatHistory(prev => ({
      ...prev,
      [currentChatId]: newMessages
    }));
  };

  const addMessageToCurrentChat = (message) => {
    setMessages(prevMessages => {
      const newMessages = [...prevMessages, message];
      setChatHistory(prev => ({
        ...prev,
        [currentChatId]: newMessages
      }));
      scrollToBottom();
      return newMessages;
    });
  };

  const addMessagesToCurrentChat = (newMessages) => {
    setMessages(prevMessages => {
      const updatedMessages = [...prevMessages, ...newMessages];
      setChatHistory(prev => ({
        ...prev,
        [currentChatId]: updatedMessages
      }));
      return updatedMessages;
    });
  };

  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      const chatContainer = document.querySelector('.chat-messages-container');
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    }, 100);
  };

  const scrollToChat = () => {
    const chatContainer = document.querySelector('.overflow-y-auto');
    if (chatContainer) {
      chatContainer.scrollTop = 0;
    }
  };

  const registerActionMessage = (actionText, actionType = 'action') => {
    const actionMessage = {
      id: Date.now(),
      text: actionText,
      sender: 'user',
      timestamp: new Date(),
      type: actionType
    };
    addMessageToCurrentChat(actionMessage);
    return actionMessage;
  };

  // Scroll to top of page on initial load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Mark animations as completed after initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationsCompleted(true);
    }, 2000); // Allow time for initial animations to complete
    
    return () => clearTimeout(timer);
  }, []);

  // NEW: Voice Recognition Functions
  const startVoiceRecognition = () => {
    if (!voiceRecognition.isSupported) {
      addProactiveMessage("🎤 Sorry, voice recognition is not supported in your browser. Please try Chrome or Edge.", 'error');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = smartFeatures.voiceCommands.language;

    recognition.onstart = () => {
      setVoiceRecognition(prev => ({ ...prev, isListening: true, transcript: '' }));
      addProactiveMessage("🎤 Listening... Speak now!", 'voice');
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript.toLowerCase();
      const confidence = event.results[0][0].confidence;
      
      setVoiceRecognition(prev => ({ 
        ...prev, 
        transcript, 
        confidence,
        isListening: false 
      }));
      
      processVoiceCommand(transcript, confidence);
    };

    recognition.onerror = (event) => {
      setVoiceRecognition(prev => ({ ...prev, isListening: false }));
      addProactiveMessage(`🎤 Voice recognition error: ${event.error}. Please try again.`, 'error');
    };

    recognition.onend = () => {
      setVoiceRecognition(prev => ({ ...prev, isListening: false }));
    };

    recognition.start();
  };

  const processVoiceCommand = (transcript, confidence) => {
    const userMessage = {
      id: Date.now(),
      text: `🎤 "${transcript}"`,
      sender: 'user',
      timestamp: new Date(),
      confidence: Math.round(confidence * 100)
    };
    addMessageToCurrentChat(userMessage);

    // Process different voice commands
    let response = '';
    
    if (transcript.includes('medication') || transcript.includes('medicine') || transcript.includes('pill')) {
      const morningMeds = patientData.medications.filter(med => med.time.includes('AM'));
      const takenCount = morningMeds.filter(med => med.taken).length;
      response = `🏥 Medication Status: You have ${morningMeds.length} morning medications. ${takenCount} taken, ${morningMeds.length - takenCount} remaining. Your medication streak is ${gamification.streaks.medication} days! 💊`;
    } else if (transcript.includes('blood pressure') || transcript.includes('bp')) {
      response = `🩺 Your latest blood pressure reading is ${smartFeatures.deviceConnections.bloodPressure.lastReading} at ${smartFeatures.deviceConnections.bloodPressure.time}. Trend: ${healthAnalytics.trends.bloodPressure}. Great job maintaining healthy levels! 📊`;
    } else if (transcript.includes('call') && (transcript.includes('daughter') || transcript.includes('maria'))) {
      response = `📞 Calling Maria Rodriguez (Daughter) at ${patientData.emergencyContact.phone}. I've also notified her that you want to talk. She was last online now and will receive an alert.`;
    } else if (transcript.includes('feel') || transcript.includes('pain') || transcript.includes('sick')) {
      response = `🔍 I'm concerned about how you're feeling. On a scale of 1-10, how would you rate your discomfort? I'm preparing to contact Dr. Martinez and Maria. Your health is my priority. Please tell me more details.`;
    } else if (transcript.includes('mood') || transcript.includes('feeling good') || transcript.includes('happy')) {
      response = `😊 Wonderful! I've recorded your positive mood. This is great for your overall health! You've maintained good spirits for ${gamification.streaks.medication} days. Keep up the excellent attitude! ⭐`;
    } else if (transcript.includes('glucose') || transcript.includes('sugar') || transcript.includes('diabetes')) {
      response = `🩸 Your latest glucose reading is ${smartFeatures.deviceConnections.glucometer.lastReading} at ${smartFeatures.deviceConnections.glucometer.time}. This is within normal range! Remember to check again 2 hours after meals. 📋`;
    } else if (transcript.includes('water') || transcript.includes('drink') || transcript.includes('hydration')) {
      response = `💧 Hydration check: You've had ${patientData.hydration.current} out of ${patientData.hydration.goal} glasses today. ${patientData.hydration.current >= patientData.hydration.goal ? 'Perfect! You\'ve reached your goal!' : 'Let\'s add another glass when you\'re ready!'} 🚰`;
    } else {
      response = `🤖 I heard: "${transcript}" (${Math.round(confidence * 100)}% confidence). I'm here to help with medications, health monitoring, calling family, or any health concerns. What can I help you with? 💙`;
    }

    // Add AI response after a delay
    setTimeout(() => {
      const aiMessage = {
        id: Date.now() + 1,
        text: response,
        sender: 'nurse',
        timestamp: new Date(),
        type: 'voice-response'
      };
      addMessageToCurrentChat(aiMessage);
    }, 1000);
  };

  const updateHealthAnalytics = () => {
    // Simulate real-time health analytics updates
    setHealthAnalytics(prev => ({
      ...prev,
      riskScore: Math.round(Math.max(10, prev.riskScore + (Math.random() - 0.5) * 5)),
      trends: {
        ...prev.trends,
        medication: gamification.streaks.medication > 10 ? 'excellent' : gamification.streaks.medication > 5 ? 'good' : 'needs_improvement'
      }
    }));
  };

  // Update analytics every 30 seconds
  useEffect(() => {
    const interval = setInterval(updateHealthAnalytics, 30000);
    return () => clearInterval(interval);
  }, [gamification.streaks.medication]);

  // Enhanced proactive reminders with animations
  useEffect(() => {
    const checkReminders = () => {
      const now = new Date();
      const currentHour = now.getHours();
      
      // Pulse notification effect
      if (currentHour === 8 || currentHour === 20) {
        setPulseNotification(true);
        setTimeout(() => setPulseNotification(false), 3000);
      }
      
      // Morning medication reminder
      if (currentHour === 8 && !currentReminder) {
        setCurrentReminder('morning-meds');
        addProactiveMessage("🌅 Good morning, Robert! It's time for your morning medications. Please take your white Metformin (500mg) and pink Lisinopril (10mg) pills. Don't forget to drink a full glass of water!", 'medication');
      }
      
      // Evening medication reminder
      if (currentHour === 20 && !currentReminder) {
        setCurrentReminder('evening-meds');
        addProactiveMessage("🌙 Good evening! Time for your blue Atorvastatin (20mg) pill. Remember to take it with dinner for better absorption.", 'medication');
      }
      
      // Hydration reminders with fun animations
      if ([10, 14, 18].includes(currentHour) && !currentReminder) {
        setCurrentReminder('hydration');
        addProactiveMessage("💧 Hydration time! You've had " + patientData.hydration.current + " glasses today. Let's aim for " + patientData.hydration.goal + "! Your kidneys and heart will thank you.", 'hydration');
      }
    };

    const interval = setInterval(checkReminders, 60000);
    return () => clearInterval(interval);
  }, [currentReminder, patientData.hydration]);

  const addProactiveMessage = (text, type) => {
    const proactiveMessage = {
      id: Date.now(),
      text,
      sender: 'nurse',
      timestamp: new Date(),
      type,
      proactive: true
    };
    addMessageToCurrentChat(proactiveMessage);
  };

  const quickActions = [
    { text: "Analyze food", icon: Camera, color: "bg-gradient-to-r from-indigo-600 to-purple-500", animation: "shake" },
    { text: "Take medication", icon: Pill, color: "bg-gradient-to-r from-blue-400 to-blue-500", animation: "bounce" },
    { text: "Record vitals", icon: Activity, color: "bg-gradient-to-r from-blue-500 to-indigo-500", animation: "pulse" },
    { text: "How I feel", icon: Smile, color: "bg-gradient-to-r from-indigo-500 to-indigo-600", animation: "wiggle" },
    { text: "Call doctor", icon: Phone, color: "bg-gradient-to-r from-purple-500 to-purple-600", animation: "ring" }
  ];

  const handleSendMessage = async () => {
    // Si no hay mensaje ni archivo, no hacer nada
    if (!inputMessage.trim() && !attachmentPreview.file) return;

    if (attachmentPreview.file) {
      // Si hay un archivo adjunto, enviar mensaje con archivo
      const userMessage = {
        id: Date.now(),
        text: inputMessage.trim() || '',  // Si no hay texto, enviar string vacío
        sender: 'user',
        timestamp: new Date(),
        attachment: {
          type: attachmentPreview.type,
          name: attachmentPreview.file.name,
          url: attachmentPreview.type === 'image' ? attachmentPreview.previewUrl : null
        }
      };

      addMessageToCurrentChat(userMessage);
      setInputMessage('');
      setIsTyping(true);

      // Resetear el estado de vista previa
      setShowAttachmentPreview(false);
      setAttachmentPreview({
        type: null,
        file: null,
        previewUrl: null,
        message: ''
      });

      // Process through Perplexity agents for file attachments
      try {
        const context = {
          hasAttachment: true,
          attachmentType: attachmentPreview.type,
          fileName: attachmentPreview.file.name,
          needsRecentInfo: attachmentPreview.type === 'image' // Images may need recent analysis capabilities
        };

        const agentResult = await processMessage(
          inputMessage.trim() || `Analyze this ${attachmentPreview.type}: ${attachmentPreview.file.name}`, 
          context
        );

        if (agentResult.success) {
          const nurseMessage = formatAgentResponse({
            ...agentResult.response,
            citations: agentResult.response.apiResponse?.citations // Pass API citations if available
          });
          addMessageToCurrentChat(nurseMessage);
        } else {
          // Fallback message
          const nurseMessage = {
            id: Date.now() + 1,
            text: `I've received your file "${attachmentPreview.file.name}". I'll process it and provide relevant information.`,
            sender: 'nurse',
            timestamp: new Date(),
            type: 'attachment-response'
          };
          addMessageToCurrentChat(nurseMessage);
        }
      } catch (error) {
        console.error('Agent processing error for attachment:', error);
        const nurseMessage = {
          id: Date.now() + 1,
          text: "I've received your file but encountered an issue processing it. Please try again or contact your healthcare provider if this is urgent.",
          sender: 'nurse',
          timestamp: new Date(),
          type: 'error'
        };
        addMessageToCurrentChat(nurseMessage);
      } finally {
        setIsTyping(false);
      }
    } else {
      // Si solo hay texto, enviar mensaje normal
      const userMessage = {
        id: Date.now(),
        text: inputMessage,
        sender: 'user',
        timestamp: new Date()
      };

      addMessageToCurrentChat(userMessage);
      const currentInput = inputMessage;
      setInputMessage('');
      setIsTyping(true);

      // Process through Perplexity agents
      try {
        const context = {
          patientData: {
            medications: patientData.medications,
            vitals: patientData.vitals,
            conditions: patientData.conditions
          },
          needsRecentInfo: currentInput.toLowerCase().includes('latest') || 
                          currentInput.toLowerCase().includes('recent') ||
                          currentInput.toLowerCase().includes('new')
        };

        const agentResult = await processMessage(currentInput, context);

        if (agentResult.success) {
          const nurseMessage = formatAgentResponse({
            ...agentResult.response,
            citations: agentResult.response.apiResponse?.citations // Pass API citations if available
          });
          
          // Add agent indicator to message
          nurseMessage.agentInfo = {
            agent: agentResult.agent,
            canEscalate: agentResult.canEscalate,
            nextSuggestedAgent: agentResult.nextSuggestedAgent
          };
          
          addMessageToCurrentChat(nurseMessage);

          // Show agent escalation options if available
          if (agentResult.canEscalate) {
            setTimeout(() => {
              const escalationMessage = {
                id: Date.now() + 2,
                text: `💡 Based on our conversation, I can connect you with a specialized ${agentResult.nextSuggestedAgent || 'healthcare'} agent for more detailed assistance. Would you like me to do that?`,
                sender: 'nurse',
                timestamp: new Date(),
                type: 'escalation-offer',
                escalationAgent: agentResult.nextSuggestedAgent
              };
              addMessageToCurrentChat(escalationMessage);
            }, 1000);
          }
        } else {
          // Fallback to original response system
          const nurseResponse = generateFallbackResponse(currentInput);
          const nurseMessage = {
            id: Date.now() + 1,
            text: nurseResponse,
            sender: 'nurse',
            timestamp: new Date(),
            type: 'fallback'
          };
          addMessageToCurrentChat(nurseMessage);
        }
      } catch (error) {
        console.error('Agent processing error:', error);
        const nurseResponse = generateFallbackResponse(currentInput);
        const nurseMessage = {
          id: Date.now() + 1,
          text: nurseResponse,
          sender: 'nurse',
          timestamp: new Date(),
          type: 'error-fallback'
        };
        addMessageToCurrentChat(nurseMessage);
      } finally {
        setIsTyping(false);
      }
    }
  };

  // Fallback response system (simplified version of original generateNurseResponse)
  const generateFallbackResponse = (userInput) => {
    const input = userInput.toLowerCase();
    
    if (input.includes('medication') || input.includes('pill')) {
      return `I see you're asking about medications. Your current medications are: Metformin (white, morning, 500mg), Lisinopril (pink, morning, 10mg), and Atorvastatin (blue, evening, 20mg). Would you like me to mark any as taken?`;
    }
    
    if (input.includes('good') || input.includes('great') || input.includes('fine')) {
      return "That's wonderful to hear! I'm so glad you're feeling good today. Remember to keep up with your medications and stay hydrated. Is there anything specific you'd like to focus on today?";
    }
    
    if (input.includes('tired') || input.includes('fatigue')) {
      return "I understand you're feeling tired. This could be related to your blood sugar or blood pressure. Have you taken your medications today? Let's also check your vitals when you're ready.";
    }
    
    if (input.includes('pain') || input.includes('hurt')) {
      return "I'm sorry you're experiencing pain. On a scale of 1-10, how would you rate it? If it's severe (7+), we should contact Dr. Martinez immediately. I'm also notifying Maria about your discomfort.";
    }
    
    return "Thank you for sharing that with me. I'm here to help you stay healthy and safe. Would you like to record your vitals, check your medications, or is there something else I can help you with?";
  };

  // Legacy function kept for compatibility but now redirects to agents
  const generateNurseResponse = (userInput) => {
    console.warn('generateNurseResponse is deprecated. Use Perplexity agents instead.');
    return generateFallbackResponse(userInput);
  };

  const handleQuickAction = (action) => {
    // Close any open modals and panels first
    setShowFoodAnalyzer(false);
    setShowVitalsInput(false);
    setShowMoodTracker(false);
    setShowBottomPanel(null);

    // Register the action as a message
    const actionText = action.text === "Take medication" ? "I want to take my medication" :
          action.text === "Record vitals" ? "I want to record my vital signs" :
          action.text === "How I feel" ? "I want to tell you how I'm feeling today" :
          action.text === "Analyze food" ? "I want to analyze my food" :
          action.text === "Call doctor" ? "I need to call my doctor" :
          action.text;
    
    registerActionMessage(actionText, 'quick-action');

    const actionResponses = {
      "Take medication": "Great! Let me help you track your medications. You can mark them as taken or manage your medication list below.",
      "Record vitals": "Perfect! Let's record your vital signs. You can update each measurement below.",
      "How I feel": "I'd love to know how you're feeling today! Your mood is important for your overall health.",
      "Analyze food": "Excellent choice! Take a photo of your meal and I'll analyze it with Perplexity AI for your diabetic and heart-healthy diet.",
      "Call doctor": "I'll help you contact Dr. Martinez. Choose how you'd like to reach out below."
    };
    
    const response = actionResponses[action.text] || "I'm here to help with that!";
    
    // Send nurse response (reduced delay from 500ms to 200ms)
    setTimeout(() => {
      const nurseMessage = {
        id: Date.now() + 1,
        text: response,
        sender: 'nurse',
        timestamp: new Date(),
        type: 'quick-action-response'
      };
      addMessageToCurrentChat(nurseMessage);
    }, 200);

    // Show appropriate panel or modal and scroll to bottom for Quick Actions
    setTimeout(() => {
      if (action.text === "Take medication") {
        setShowBottomPanel('medication');
      } else if (action.text === "Record vitals") {
        setShowBottomPanel('vitals');
      } else if (action.text === "Call doctor") {
        setShowBottomPanel('doctor');
      } else if (action.text === "Analyze food") {
        setShowFoodAnalyzer(true);
      } else if (action.text === "How I feel") {
        setShowMoodTracker(true);
      }
      
      // For Quick Actions, scroll to bottom to show the panels
      scrollToBottom();
    }, 400);
  };

  const handleMedicationTaken = (medId) => {
    setPatientData(prev => ({
      ...prev,
      medications: prev.medications.map(med => 
        med.id === medId 
          ? { ...med, taken: true, lastTaken: new Date(), remaining: med.remaining - 1 }
          : med
      )
    }));
    
    // Only celebrate when medication is taken
    setCelebrateAction('medication');
    setTimeout(() => setCelebrateAction(null), 3000);
    
    const nurseMessage = {
      id: Date.now(),
      text: "🎉 Excellent! I've marked your medication as taken. Great job staying on track with your health routine! I've notified Dr. Martinez and Maria.",
      sender: 'nurse',
      timestamp: new Date(),
      type: 'celebration'
    };
    addMessageToCurrentChat(nurseMessage);
  };

  const handleDrinkWater = () => {
    setPatientData(prev => ({
      ...prev,
      hydration: {
        ...prev.hydration,
        current: Math.min(prev.hydration.current + 1, prev.hydration.goal),
        lastDrink: new Date()
      }
    }));
    
    // Send nurse response about hydration
    setTimeout(() => {
      const newCurrent = Math.min(patientData.hydration.current + 1, patientData.hydration.goal);
      const nurseMessage = {
        id: Date.now(),
        text: `Great job staying hydrated! You've now had ${newCurrent} out of ${patientData.hydration.goal} glasses today. ${newCurrent >= patientData.hydration.goal ? "🎉 You've reached your daily hydration goal!" : "Keep up the good work!"} Your kidneys and heart will thank you.`,
        sender: 'nurse',
        timestamp: new Date(),
        type: 'hydration'
      };
      addMessageToCurrentChat(nurseMessage);
    }, 500);
  };

  const handleEmergencyCall = () => {
    // If emergency has been completed or countdown is active, just return
    if (emergencyCall.completed || emergencyCall.countdown > 0) return;

    // Start countdown from 5
    setEmergencyCall(prev => ({
      ...prev,
      countdown: 5
    }));

    // Start countdown interval - exactly 1 second per number
    const countdownInterval = setInterval(() => {
      setEmergencyCall(prev => {
        if (prev.countdown <= 1) {
          clearInterval(countdownInterval);
          // If not cancelled, proceed with emergency
          if (prev.countdown === 1) {
            return {
              ...prev,
              countdown: 0,
              calling: true,
              hasBeenTriggered: true
            };
          }
          return prev;
        }
        return {
          ...prev,
          countdown: prev.countdown - 1
        };
      });
    }, 1000); // Exactly 1 second per number

    // If not cancelled after countdown, proceed with emergency call
    setTimeout(() => {
      setEmergencyCall(prev => {
        if (prev.calling && prev.hasBeenTriggered) {
          // Show emergency modal
    setShowEmergencyModal(true);
    
          // Simulate calling 911 (1 second)
    setTimeout(() => {
      setEmergencyCall(prev => ({ ...prev, calling: false, sendingData: true }));

            // Simulate sending medical data (1 second)
      setTimeout(() => {
        // Complete the emergency sequence
        setTimeout(() => {
          setEmergencyCall(prev => ({ ...prev, sendingData: false, completed: true }));

                // Add emergency message to chat
                addMessageToCurrentChat({
                  id: Date.now(),
                  sender: 'assistant',
                  text: "🚨 [TEST MODE] Emergency services have been notified. Help is on the way. Stay calm and follow these steps:\n\n1. Remain in your current location if safe\n2. Keep your phone nearby\n3. Unlock your front door if possible\n4. Gather any relevant medical documents\n\nEmergency contacts are being notified. Maria (Primary Caregiver) has been alerted and is on her way.",
                  timestamp: new Date(),
                  type: 'emergency'
                });
              }, 1000);
            }, 1000);
          }, 1000);
        }
        return prev;
      });
    }, 5000); // Total countdown time (5 seconds)
  };

  const handleFileSelect = (file) => {
    if (file) {
      setAttachmentPreview({
        type: 'file',
        file: file,
        previewUrl: null,
        message: ''
      });
      setShowAttachmentPreview(true);
      setInputMessage('Update my medical information');
    }
  };

  const handleImageSelect = (file) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachmentPreview({
          type: 'image',
          file: file,
          previewUrl: reader.result,
          message: ''
        });
        setShowAttachmentPreview(true);
        setInputMessage('Analyze this image');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAttachmentCancel = () => {
    setShowAttachmentPreview(false);
    setAttachmentPreview({
      type: null,
      file: null,
      previewUrl: null,
      message: ''
    });
    setInputMessage('');
  };

  const handleModalClose = (modalType) => {
    // Send cancellation message
    const userMessage = {
      id: Date.now(),
      text: modalType === 'vitals' ? "I've decided not to record my vitals right now" :
            modalType === 'mood' ? "I've decided not to share my mood right now" :
            modalType === 'food' ? "I've decided not to analyze my food right now" :
            "I've closed the modal",
      sender: 'user',
      timestamp: new Date()
    };
    addMessageToCurrentChat(userMessage);

    // Send nurse response
    setTimeout(() => {
      const nurseMessage = {
        id: Date.now() + 1,
        text: "No problem at all! I'm here whenever you're ready. Feel free to ask me anything or use the quick actions when you need help.",
        sender: 'nurse',
        timestamp: new Date(),
        type: 'modal-close'
      };
      addMessageToCurrentChat(nurseMessage);
    }, 500);

    // Close the appropriate modal
    if (modalType === 'vitals') setShowVitalsInput(false);
    if (modalType === 'mood') setShowMoodTracker(false);
    if (modalType === 'food') setShowFoodAnalyzer(false);
  };

  const VitalsInput = () => {
    const [vitals, setVitals] = useState({
      temperature: '98.6',
      heartRate: '72',
      weight: '165',
      bloodPressure: '120/80'
    });

    const handleSubmit = () => {
      setPatientData(prev => ({
        ...prev,
        vitals: {
          ...vitals,
          lastUpdated: new Date()
        }
      }));
      
      setShowVitalsInput(false);
      
      // Send user message about completing vitals
      const userMessage = {
        id: Date.now(),
        text: `I've recorded my vital signs: Temperature ${vitals.temperature}°F, Heart Rate ${vitals.heartRate} bpm, Weight ${vitals.weight} lbs, Blood Pressure ${vitals.bloodPressure}`,
        sender: 'user',
        timestamp: new Date()
      };
      addMessageToCurrentChat(userMessage);
      
      // Send nurse response
      setTimeout(() => {
        const nurseMessage = {
          id: Date.now() + 1,
          text: "Perfect! I've recorded your vital signs. Everything looks good! I've shared this information with Dr. Martinez and Maria. Keep up the great work!",
          sender: 'nurse',
          timestamp: new Date(),
          type: 'vitals-recorded'
        };
        addMessageToCurrentChat(nurseMessage);
      }, 1000);
    };

    const vitalItems = [
      { 
        key: 'temperature', 
        label: 'Temperature', 
        value: vitals.temperature, 
        unit: '°F', 
        icon: Thermometer, 
        color: 'text-red-500',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      },
      { 
        key: 'heartRate', 
        label: 'Heart Rate', 
        value: vitals.heartRate, 
        unit: 'bpm', 
        icon: Heart, 
        color: 'text-pink-500',
        bgColor: 'bg-pink-50',
        borderColor: 'border-pink-200'
      },
      { 
        key: 'bloodPressure', 
        label: 'Blood Pressure', 
        value: vitals.bloodPressure, 
        unit: '', 
        icon: Stethoscope, 
        color: 'text-blue-500',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200'
      },
      { 
        key: 'weight', 
        label: 'Weight', 
        value: vitals.weight, 
        unit: 'lbs', 
        icon: Scale, 
        color: 'text-orange-500',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200'
      }
    ];

  return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ 
          type: "spring", 
          stiffness: 500, 
          damping: 30,
          duration: 0.2
        }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      >
        <div className="bg-white p-6 rounded-2xl shadow-2xl border border-gray-100 max-w-md w-full">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Activity className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 text-lg">Vital Signs Update</h3>
            </div>
            <button
              onClick={() => handleModalClose('vitals')}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            {vitalItems.map((vital, index) => (
              <div key={vital.key} className="text-center">
                <div className={`${vital.bgColor} ${vital.borderColor} border-2 rounded-2xl p-4 mb-2`}>
                  <vital.icon className={`w-8 h-8 ${vital.color} mx-auto mb-2`} />
                  <h4 className="text-sm font-medium text-gray-700 mb-2">{vital.label}</h4>
                  <div className="text-2xl font-bold text-blue-600 mb-1">
                    {vital.value}<span className="text-sm text-gray-500">{vital.unit}</span>
                  </div>
                  <motion.button
                    onClick={() => {
                      const newValue = prompt(`Enter new ${vital.label.toLowerCase()}:`, vital.value);
                      if (newValue) {
                        setVitals(prev => ({ ...prev, [vital.key]: newValue }));
                      }
                    }}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1 rounded-lg text-sm font-medium transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Update
                  </motion.button>
                </div>
              </div>
            ))}
          </div>

          <motion.button
            onClick={handleSubmit}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-3 px-4 rounded-xl text-lg font-medium shadow-lg"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Save All Vitals
          </motion.button>
        </div>
      </motion.div>
    );
  };

  const MoodTracker = () => {
    const moods = [
      { emoji: "😊", label: "Great", value: "great", color: "bg-green-500" },
      { emoji: "🙂", label: "Good", value: "good", color: "bg-blue-500" },
      { emoji: "😐", label: "Okay", value: "okay", color: "bg-yellow-500" },
      { emoji: "😔", label: "Not Good", value: "bad", color: "bg-orange-500" },
      { emoji: "😢", label: "Terrible", value: "terrible", color: "bg-red-500" }
    ];

    const handleMoodSelect = (mood) => {
      setPatientData(prev => ({
        ...prev,
        mood: {
          current: mood.value,
          history: [...prev.mood.history, { mood: mood.value, date: new Date() }]
        }
      }));
      
      setShowMoodTracker(false);
      
      // Send user message about mood
      const userMessage = {
        id: Date.now(),
        text: `I'm feeling ${mood.label.toLowerCase()} today ${mood.emoji}`,
        sender: 'user',
        timestamp: new Date()
      };
      addMessageToCurrentChat(userMessage);
      
      // Send nurse response
      setTimeout(() => {
        const nurseMessage = {
          id: Date.now() + 1,
          text: `Thank you for sharing! I've recorded that you're feeling ${mood.label.toLowerCase()} today. ${mood.value === 'great' || mood.value === 'good' ? "That's wonderful!" : "I'm here to support you. Would you like to talk about it or contact Dr. Martinez?"} I've updated your mood in your health profile.`,
          sender: 'nurse',
          timestamp: new Date(),
          type: 'mood-response'
        };
        addMessageToCurrentChat(nurseMessage);
      }, 1000);
    };

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ 
          type: "spring", 
          stiffness: 500, 
          damping: 30,
          duration: 0.2
        }}
        className="bg-white p-6 rounded-xl shadow-lg border border-gray-100"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="bg-yellow-100 p-2 rounded-lg">
              <Smile className="w-5 h-5 text-yellow-600" />
            </div>
            <h3 className="font-semibold text-gray-900">How are you feeling today?</h3>
          </div>
          <button
            onClick={() => handleModalClose('mood')}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-5 gap-3">
          {moods.map((mood, index) => (
            <motion.button
              key={mood.value}
              onClick={() => handleMoodSelect(mood)}
              className="flex flex-col items-center p-4 rounded-lg border-2 border-gray-200 hover:border-gray-400 transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <span className="text-3xl mb-2">{mood.emoji}</span>
              <span className="text-sm font-medium text-gray-700">{mood.label}</span>
            </motion.button>
          ))}
        </div>
      </motion.div>
    );
  };

  const FoodAnalyzer = () => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [analysis, setAnalysis] = useState(null);

    const handleImageUpload = (event) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setSelectedImage(e.target.result);
        };
        reader.readAsDataURL(file);
      }
    };

    const analyzeFood = () => {
      setAnalyzing(true);
      
      setTimeout(() => {
        const mockAnalysis = {
          foodItems: ['Grilled chicken breast', 'Steamed broccoli', 'Brown rice'],
          nutrition: {
            calories: 420,
            carbs: 45,
            protein: 35,
            fat: 8,
            sodium: 380,
            fiber: 6
          },
          recommendations: [
            { type: 'good', text: 'Excellent protein choice for muscle maintenance' },
            { type: 'good', text: 'High fiber content supports digestive health' },
            { type: 'warning', text: 'Consider reducing sodium for blood pressure management' },
            { type: 'info', text: 'Perfect timing with Metformin - take medication now' }
          ],
          diabeticFriendly: true,
          heartHealthy: true
        };
        
        setAnalysis(mockAnalysis);
        setAnalyzing(false);
        
        // Send user message about food analysis
        const userMessage = {
          id: Date.now(),
          text: "I've analyzed my meal with the AI food analyzer",
          sender: 'user',
          timestamp: new Date()
        };
        addMessageToCurrentChat(userMessage);
        
        // Send nurse response with analysis
        setTimeout(() => {
          const analysisMessage = {
            id: Date.now() + 1,
            text: `🍽️ Food Analysis Complete! Your meal contains ${mockAnalysis.nutrition.calories} calories and ${mockAnalysis.nutrition.carbs}g carbs - perfect for your diabetic diet! The low sodium content is great for your blood pressure. I've shared this with Dr. Martinez and Maria.`,
            sender: 'nurse',
            timestamp: new Date(),
            type: 'food-analysis'
          };
          addMessageToCurrentChat(analysisMessage);
        }, 1000);
      }, 3000);
    };

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ 
          type: "spring", 
          stiffness: 500, 
          damping: 30,
          duration: 0.2
        }}
        className="bg-white p-6 rounded-xl shadow-lg border border-gray-100"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <Camera className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-900">AI Food Analyzer</h3>
          </div>
          <button
            onClick={() => handleModalClose('food')}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!selectedImage ? (
          <motion.div 
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center"
            whileHover={{ borderColor: '#9333ea' }}
          >
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 2 }}
            >
              <Camera className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            </motion.div>
            <p className="text-gray-600 mb-4">Take a photo of your meal for AI nutritional analysis</p>
            <label className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-lg cursor-pointer inline-flex items-center space-x-2 text-lg">
              <Upload className="w-5 h-5" />
              <span>Upload Photo</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
          </motion.div>
        ) : (
          <div className="space-y-4">
            <motion.div 
              className="relative"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
            >
              <img
                src={selectedImage}
                alt="Food to analyze"
                className="w-full h-48 object-cover rounded-lg"
              />
              <motion.button
                onClick={() => setSelectedImage(null)}
                className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <X className="w-4 h-4" />
              </motion.button>
            </motion.div>

            {!analysis && !analyzing && (
              <motion.button
                onClick={analyzeFood}
                className="w-full bg-purple-500 hover:bg-purple-600 text-white py-3 px-4 rounded-lg flex items-center justify-center space-x-2 text-lg"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Zap className="w-5 h-5" />
                <span>Analyze with Perplexity AI</span>
              </motion.button>
            )}

            {analyzing && (
              <div className="text-center py-8">
                <motion.div 
                  className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1 }}
                />
                <p className="text-gray-600 text-lg">Analyzing your meal with Perplexity AI...</p>
              </div>
            )}

            {analysis && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(analysis.nutrition).map(([key, value], index) => (
                    <motion.div
                      key={key}
                      className="bg-gray-50 p-3 rounded-lg text-center"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <p className="text-xs text-gray-600 capitalize">{key}</p>
                      <p className="text-lg font-semibold">{value}{key === 'calories' ? '' : key === 'carbs' || key === 'protein' || key === 'fat' || key === 'fiber' ? 'g' : 'mg'}</p>
                    </motion.div>
                  ))}
                </div>

                <div className="flex space-x-2 justify-center">
                  {analysis.diabeticFriendly && (
                    <motion.span 
                      className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      ✅ Diabetic Friendly
                    </motion.span>
                  )}
                  {analysis.heartHealthy && (
                    <motion.span 
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.7 }}
                    >
                      ❤️ Heart Healthy
                    </motion.span>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        )}
      </motion.div>
    );
  };

  const EmergencyModal = () => {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
        onClick={() => setShowEmergencyModal(false)}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-red-500 text-white p-3 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="text-2xl"
              >
                🚨
              </motion.div>
              <div>
                <h2 className="text-lg font-bold">Emergency Protocol</h2>
                <p className="text-red-100 text-xs">DEMO MODE - NOT FUNCTIONAL</p>
              </div>
            </div>
            <div className="bg-yellow-50 rounded-lg px-2 py-1 flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600" />
              <p className="text-yellow-800 text-xs font-medium">Demo Only - Call 911 for Real Emergencies</p>
              </div>
            </div>

          {/* Content */}
          <div className="p-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Left Column: Emergency Process */}
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900 text-sm mb-2">Emergency Process</h3>
                  {/* Step 1: Calling */}
                <div className={`flex items-center space-x-2 p-2 rounded-lg ${
                    emergencyCall.calling ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
                  }`}>
                    {emergencyCall.calling ? (
                      <motion.div
                      className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1 }}
                      />
                    ) : (
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                        emergencyCall.calling || emergencyCall.sendingData || emergencyCall.completed ? 'bg-green-500' : 'bg-gray-300'
                      }`}>
                      {(emergencyCall.sendingData || emergencyCall.completed) && <Check className="w-2 h-2 text-white" />}
                      </div>
                    )}
                    <div className="flex-1">
                    <p className="font-medium text-sm">Calling Emergency Services</p>
                    <p className="text-xs text-gray-600">Contacting 911 (Demo)</p>
                    </div>
                  </div>

                  {/* Step 2: Sending Data */}
                <div className={`flex items-center space-x-2 p-2 rounded-lg ${
                    emergencyCall.sendingData ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'
                  }`}>
                    {emergencyCall.sendingData ? (
                      <motion.div
                      className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 1 }}
                    />
                  ) : (
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                        emergencyCall.sendingData || emergencyCall.completed ? 'bg-green-500' : 'bg-gray-300'
                      }`}>
                      {emergencyCall.completed && <Check className="w-2 h-2 text-white" />}
    </div>
                    )}
                    <div className="flex-1">
                    <p className="font-medium text-sm">Sending Medical Data</p>
                    <p className="text-xs text-gray-600">Transmitting patient information</p>
                    </div>
                  </div>

                {/* Step 3: Notifying */}
                <div className={`flex items-center space-x-2 p-2 rounded-lg ${
                    emergencyCall.completed ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                  }`}>
                    {emergencyCall.completed ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                        emergencyCall.completed ? 'bg-green-500' : 'bg-gray-300'
                      }`}>
                      {emergencyCall.completed && <Check className="w-2 h-2 text-white" />}
                      </div>
                    )}
                    <div className="flex-1">
                    <p className="font-medium text-sm">Notifying Emergency Contacts</p>
                    <p className="text-xs text-gray-600">Alerting Maria Rodriguez & Dr. Martinez</p>
                  </div>
                </div>

                {/* Success Message */}
                {emergencyCall.completed && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-50 border border-green-200 rounded-lg p-2 text-center"
                  >
                    <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
                    <h4 className="font-medium text-green-800 text-sm">Protocol Complete</h4>
                    <p className="text-xs text-green-700">Help would be on the way in a real emergency</p>
                  </motion.div>
                )}
              </div>

              {/* Right Column: Medical Summary */}
              <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                <h3 className="font-semibold text-gray-900 text-sm mb-2">Medical Summary</h3>
                
                <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                    <p className="text-gray-600 text-xs">Patient:</p>
                    <p className="font-medium">Robert, Age 72</p>
                    </div>
                  <div>
                    <p className="text-gray-600 text-xs">Location:</p>
                    <p className="font-medium">Home</p>
                    </div>
                    </div>
                    
                    <div>
                  <p className="text-gray-600 text-xs mb-1">Medical Conditions:</p>
                  <div className="flex flex-wrap gap-1">
                    <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs">Type 2 Diabetes</span>
                    <span className="bg-orange-100 text-orange-800 px-2 py-0.5 rounded text-xs">Hypertension</span>
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs">High Cholesterol</span>
                      </div>
                    </div>
                    
                    <div>
                  <p className="text-gray-600 text-xs mb-1">Current Medications:</p>
                  <div className="grid grid-cols-3 gap-1">
                    <div className="bg-blue-50 p-1 rounded">
                      <p className="text-xs font-medium">Metformin</p>
                      <p className="text-xs text-gray-600">500mg</p>
                          </div>
                    <div className="bg-blue-50 p-1 rounded">
                      <p className="text-xs font-medium">Lisinopril</p>
                      <p className="text-xs text-gray-600">10mg</p>
                    </div>
                    <div className="bg-blue-50 p-1 rounded">
                      <p className="text-xs font-medium">Atorvastatin</p>
                      <p className="text-xs text-gray-600">20mg</p>
                    </div>
                      </div>
                    </div>
                    
                    <div>
                  <p className="text-gray-600 text-xs mb-1">Last Vital Signs:</p>
                  <div className="grid grid-cols-4 gap-1">
                    <div className="bg-green-50 p-1 rounded text-center">
                      <p className="text-xs font-medium">98.6°F</p>
                      <p className="text-xs text-gray-600">Temp</p>
                        </div>
                    <div className="bg-green-50 p-1 rounded text-center">
                      <p className="text-xs font-medium">120/80</p>
                      <p className="text-xs text-gray-600">BP</p>
                        </div>
                    <div className="bg-green-50 p-1 rounded text-center">
                      <p className="text-xs font-medium">72 bpm</p>
                      <p className="text-xs text-gray-600">HR</p>
                        </div>
                    <div className="bg-green-50 p-1 rounded text-center">
                      <p className="text-xs font-medium">165 lbs</p>
                      <p className="text-xs text-gray-600">Weight</p>
                        </div>
                      </div>
                    </div>

                    <div>
                  <p className="text-gray-600 text-xs mb-1">Emergency Contacts:</p>
                  <div className="space-y-1">
                    <div className="bg-purple-50 p-1 rounded flex justify-between items-center">
                      <div>
                        <p className="text-xs font-medium">Maria Rodriguez</p>
                        <p className="text-xs text-gray-600">Daughter</p>
                        </div>
                      <p className="text-xs font-medium">+1 (555) 123-4567</p>
                        </div>
                    <div className="bg-purple-50 p-1 rounded flex justify-between items-center">
                      <div>
                        <p className="text-xs font-medium">Dr. Martinez</p>
                        <p className="text-xs text-gray-600">Primary Care</p>
                      </div>
                      <p className="text-xs font-medium">+1 (555) 987-6543</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 rounded-b-2xl flex justify-end space-x-2 border-t">
              {emergencyCall.completed && (
                <motion.button
                  onClick={() => {
                    setEmergencyCall({ calling: true, sendingData: false, completed: false, hasBeenTriggered: true });
                    setTimeout(() => {
                      setEmergencyCall(prev => ({ ...prev, calling: false, sendingData: true }));
                      setTimeout(() => {
                        setTimeout(() => {
                          setEmergencyCall(prev => ({ ...prev, sendingData: false, completed: true }));
                        }, 2000);
                      }, 4000);
                    }, 3000);
                  }}
                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded text-sm"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Test Again
                </motion.button>
              )}
              <motion.button
                onClick={() => setShowEmergencyModal(false)}
              className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-sm"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Close
              </motion.button>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  const BottomPanel = () => {
    if (!showBottomPanel) return null;

    const handleClosePanel = () => {
      setShowBottomPanel(null);
      
      const userMessage = {
        id: Date.now(),
        text: "I've closed the panel",
        sender: 'user',
        timestamp: new Date()
      };
      addMessageToCurrentChat(userMessage);

      setTimeout(() => {
        const nurseMessage = {
          id: Date.now() + 1,
          text: "No problem! I'm here whenever you need help. Feel free to use the quick actions anytime.",
          sender: 'nurse',
          timestamp: new Date(),
          type: 'panel-close'
        };
        addMessageToCurrentChat(nurseMessage);
      }, 500);
    };

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ 
          type: "spring", 
          stiffness: 500, 
          damping: 30,
          duration: 0.2
        }}
        className="bg-white p-6 rounded-xl shadow-lg border border-gray-100"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${
              showBottomPanel === 'medication' ? 'bg-green-100' :
              showBottomPanel === 'vitals' ? 'bg-blue-100' :
              showBottomPanel === 'doctor' ? 'bg-purple-100' :
              'bg-cyan-100'
            }`}>
              {showBottomPanel === 'medication' && <Pill className="w-5 h-5 text-green-600" />}
              {showBottomPanel === 'vitals' && <Activity className="w-5 h-5 text-blue-600" />}
              {showBottomPanel === 'doctor' && <User className="w-5 h-5 text-purple-600" />}
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {showBottomPanel === 'medication' && 'Medication Management'}
              {showBottomPanel === 'vitals' && 'Vital Signs Update'}
              {showBottomPanel === 'doctor' && 'Contact Dr. Martinez'}
              {showBottomPanel === 'water' && 'Hydration Tracker'}
            </h3>
          </div>
          <button
            onClick={handleClosePanel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Medication Panel */}
        {showBottomPanel === 'medication' && (
          <div className="grid md:grid-cols-3 gap-4">
            {patientData.medications.map((med, index) => (
              <div key={med.id} className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-4 h-4 rounded-full border-2 border-gray-300"
                      style={{ backgroundColor: med.color }}
                    ></div>
                    <span className="font-medium">{med.name}</span>
                  </div>
                  <span className="text-sm text-gray-600">{med.dosage}</span>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">Time: {med.time}</p>
                  <p className="text-sm text-gray-600">Remaining: {med.remaining} pills</p>
                  <div className="flex space-x-2">
                    <motion.button
                      onClick={() => handleMedicationTaken(med.id)}
                      disabled={med.taken}
                      className={`flex-1 py-2 px-3 rounded text-sm ${
                        med.taken 
                          ? 'bg-green-100 text-green-800 cursor-not-allowed' 
                          : 'bg-green-500 hover:bg-green-600 text-white'
                      }`}
                      whileHover={!med.taken ? { scale: 1.05 } : {}}
                      whileTap={!med.taken ? { scale: 0.95 } : {}}
                      transition={{ type: "spring", stiffness: 400, damping: 20 }}
                    >
                      {med.taken ? '✓ Taken' : 'Mark as Taken'}
                    </motion.button>
                    <motion.button
                      onClick={() => {
                        setPatientData(prev => ({
                          ...prev,
                          medications: prev.medications.map(m => 
                            m.id === med.id 
                              ? { ...m, remaining: m.remaining + 1 }
                              : m
                          )
                        }));
                        
                        const userMessage = {
                          id: Date.now(),
                          text: `I've added one more ${med.name} pill to my supply`,
                          sender: 'user',
                          timestamp: new Date()
                        };
                        addMessageToCurrentChat(userMessage);
                        
                        setTimeout(() => {
                          const nurseMessage = {
                            id: Date.now() + 1,
                            text: `Great! I've updated your ${med.name} supply. You now have ${med.remaining + 1} pills remaining. I've notified Dr. Martinez about this update.`,
                            sender: 'nurse',
                            timestamp: new Date(),
                            type: 'medication-update'
                          };
                          addMessageToCurrentChat(nurseMessage);
                        }, 500);
                      }}
                      className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ type: "spring", stiffness: 400, damping: 20 }}
                    >
                      +
                    </motion.button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Vitals Panel */}
        {showBottomPanel === 'vitals' && (
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { label: 'Temperature', value: patientData.vitals.temperature, unit: '°F', icon: '🌡️' },
              { label: 'Heart Rate', value: patientData.vitals.heartRate, unit: 'bpm', icon: '❤️' },
              { label: 'Blood Pressure', value: patientData.vitals.bloodPressure, unit: '', icon: '🩺' },
              { label: 'Weight', value: patientData.vitals.weight, unit: 'lbs', icon: '⚖️' }
            ].map((vital, index) => (
              <div key={vital.label} className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="text-2xl mb-2">{vital.icon}</div>
                <h4 className="font-medium text-gray-900 mb-2">{vital.label}</h4>
                <p className="text-lg font-semibold text-blue-600 mb-3">{vital.value}</p>
                <motion.button
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-3 rounded text-sm"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                  onClick={() => {
                    setShowBottomPanel(null);
                    setShowVitalsInput(true);
                  }}
                >
                  Update
                </motion.button>
              </div>
            ))}
          </div>
        )}

        {/* Doctor Panel */}
        {showBottomPanel === 'doctor' && (
          <div className="grid md:grid-cols-4 gap-4">
            <motion.button
              onClick={() => {
                const userMessage = {
                  id: Date.now(),
                  text: "I'm calling Dr. Martinez now",
                  sender: 'user',
                  timestamp: new Date()
                };
                addMessageToCurrentChat(userMessage);
                
                setTimeout(() => {
                  const nurseMessage = {
                    id: Date.now() + 1,
                    text: "📞 Connecting you to Dr. Martinez at +1 (555) 987-6543. I've prepared your recent health data including vital signs, medications, and mood to share during the call.",
                    sender: 'nurse',
                    timestamp: new Date(),
                    type: 'doctor-call'
                  };
                  addMessageToCurrentChat(nurseMessage);
                }, 200);
                
                setShowBottomPanel(null);
              }}
              className="bg-green-500 hover:bg-green-600 text-white p-4 rounded-lg text-center"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
            >
              <Phone className="w-6 h-6 mx-auto mb-2" />
              <span className="text-sm font-medium">Call Now</span>
              <p className="text-xs mt-1 opacity-90">Direct call</p>
            </motion.button>
            
            <motion.button
              onClick={() => {
                const userMessage = {
                  id: Date.now(),
                  text: "I want to schedule an appointment with Dr. Martinez",
                  sender: 'user',
                  timestamp: new Date()
                };
                addMessageToCurrentChat(userMessage);
                
                setTimeout(() => {
                  const nurseMessage = {
                    id: Date.now() + 1,
                    text: "📅 I'm checking Dr. Martinez's availability. Based on your current health status, I recommend scheduling within the next week. Your next appointment is currently set for January 15th. Would you like to schedule an earlier visit?",
                    sender: 'nurse',
                    timestamp: new Date(),
                    type: 'doctor-schedule'
                  };
                  addMessageToCurrentChat(nurseMessage);
                }, 200);
                
                setShowBottomPanel(null);
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white p-4 rounded-lg text-center"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
            >
              <Calendar className="w-6 h-6 mx-auto mb-2" />
              <span className="text-sm font-medium">Schedule</span>
              <p className="text-xs mt-1 opacity-90">Book appointment</p>
            </motion.button>
            
            <motion.button
              onClick={() => {
                const userMessage = {
                  id: Date.now(),
                  text: "I want to send a text message to Dr. Martinez",
                  sender: 'user',
                  timestamp: new Date()
                };
                addMessageToCurrentChat(userMessage);
                
                setTimeout(() => {
                  const nurseMessage = {
                    id: Date.now() + 1,
                    text: "💬 Sending SMS to Dr. Martinez: 'Patient Robert checking in. Recent vitals: BP 120/80, HR 72bpm, Temp 98.6°F. All medications taken as prescribed. Feeling good today. Any concerns to discuss?' Message sent successfully!",
                    sender: 'nurse',
                    timestamp: new Date(),
                    type: 'doctor-sms'
                  };
                  addMessageToCurrentChat(nurseMessage);
                }, 200);
                
                setShowBottomPanel(null);
              }}
              className="bg-purple-500 hover:bg-purple-600 text-white p-4 rounded-lg text-center"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
            >
              <MessageSquare className="w-6 h-6 mx-auto mb-2" />
              <span className="text-sm font-medium">Send SMS</span>
              <p className="text-xs mt-1 opacity-90">Text message</p>
            </motion.button>
            
            <motion.button
              onClick={() => {
                const userMessage = {
                  id: Date.now(),
                  text: "I want to send my medical report to Dr. Martinez",
                  sender: 'user',
                  timestamp: new Date()
                };
                addMessageToCurrentChat(userMessage);
                
                setTimeout(() => {
                  const nurseMessage = {
                    id: Date.now() + 1,
                    text: "📋 Comprehensive medical report sent to Dr. Martinez including: Current medications (Metformin, Lisinopril, Atorvastatin), recent vital signs, mood tracking, hydration levels, and medication adherence. Report delivered successfully to his secure portal.",
                    sender: 'nurse',
                    timestamp: new Date(),
                    type: 'doctor-report'
                  };
                  addMessageToCurrentChat(nurseMessage);
                }, 200);
                
                setShowBottomPanel(null);
              }}
              className="bg-orange-500 hover:bg-orange-600 text-white p-4 rounded-lg text-center"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
            >
              <Upload className="w-6 h-6 mx-auto mb-2" />
              <span className="text-sm font-medium">Send Report</span>
              <p className="text-xs mt-1 opacity-90">Medical summary</p>
            </motion.button>
          </div>
        )}

        {/* Water Panel */}
        {false && showBottomPanel === 'water' && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-4xl">💧</div>
              <div>
                <h4 className="font-medium text-gray-900">Daily Hydration</h4>
                <p className="text-sm text-gray-600">
                  {patientData.hydration.current} of {patientData.hydration.goal} glasses today
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex space-x-1">
                {[...Array(patientData.hydration.goal)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-8 h-8 rounded-full border-2 ${
                      i < patientData.hydration.current 
                        ? 'bg-cyan-400 border-cyan-500' 
                        : 'bg-gray-100 border-gray-300'
                    }`}
                  />
                ))}
              </div>
              
              <div className="flex space-x-2">
                <motion.button
                  onClick={() => {
                    if (patientData.hydration.current > 0) {
                      setPatientData(prev => ({
                        ...prev,
                        hydration: {
                          ...prev.hydration,
                          current: prev.hydration.current - 1
                        }
                      }));
                      
                      const userMessage = {
                        id: Date.now(),
                        text: "I've removed one glass from my hydration tracker",
                        sender: 'user',
                        timestamp: new Date()
                      };
                      addMessageToCurrentChat(userMessage);
                      
                      setTimeout(() => {
                        const nurseMessage = {
                          id: Date.now() + 1,
                          text: `I've updated your hydration tracker. You now have ${patientData.hydration.current - 1} out of ${patientData.hydration.goal} glasses today. Remember to stay hydrated for your health!`,
                          sender: 'nurse',
                          timestamp: new Date(),
                          type: 'hydration-update'
                        };
                        addMessageToCurrentChat(nurseMessage);
                      }, 200);
                    }
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                >
                  <Minus className="w-4 h-4" />
                </motion.button>
                
                <motion.button
                  onClick={() => {
                    handleDrinkWater();
                    setShowBottomPanel(null);
                  }}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                >
                  Add Glass
                </motion.button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    );
  };

  const HydrationTracker = () => {
    const percentage = (patientData.hydration.current / patientData.hydration.goal) * 100;
    
    return (
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">Daily Hydration</h4>
          <span className="text-sm text-gray-600">
            {patientData.hydration.current}/{patientData.hydration.goal} glasses
          </span>
        </div>
        
        <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-cyan-400 to-blue-500"
            initial={{ width: 0 }}
            animate={{ width: `${percentage}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
          />
        </div>
        
        <div className="flex justify-between mt-2">
          {[...Array(patientData.hydration.goal)].map((_, i) => (
            <motion.div
              key={i}
              className={`w-6 h-6 rounded-full border-2 ${
                i < patientData.hydration.current 
                  ? 'bg-cyan-400 border-cyan-500' 
                  : 'bg-gray-100 border-gray-300'
              }`}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: i * 0.1 }}
            />
          ))}
        </div>
      </div>
    );
  };

  // Chat History Component
  const ChatHistoryPanel = () => {
    if (!showChatHistory) return null;

    return (
      <motion.div
        initial={{ opacity: 0, x: 250 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 250 }}
        className="absolute top-0 right-0 h-full w-64 bg-white shadow-2xl backdrop-blur-sm z-40 border-l border-gray-200/50 rounded-r-3xl overflow-hidden"
        style={{ position: 'absolute', right: 0, top: 0, height: '100%' }}
      >
        <div className="flex flex-col h-full">
          {/* Compact Header */}
          <div className="flex items-center justify-between p-3 border-b  bg-gradient-to-r from-blue-50/80 to-indigo-50/80">
            <h3 className="font-semibold text-gray-900 text-sm flex items-center">
              <MessageSquare className="w-4 h-4 mr-2 text-blue-600" />
              Chat History
            </h3>
            <motion.button
              onClick={() => setShowChatHistory(false)}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <X className="w-4 h-4 text-gray-500" />
            </motion.button>
          </div>

          {/* Simple Chat List - Only Titles */}
          <div className="flex-1 overflow-y-auto p-2">
            <div className="space-y-1.5">
              {Object.entries(chatHistory).map(([chatId, messages]) => {
                const isActive = chatId === currentChatId;
                
                return (
                  <motion.button
                    key={chatId}
                    onClick={() => switchToChat(chatId)}
                    className={`w-full text-left p-2.5 rounded-lg border transition-all duration-200 ${
                      isActive 
                        ? 'bg-blue-50 border-blue-200' 
                        : 'bg-white/60 border-gray-200/50 hover:bg-gray-50 hover:border-gray-300'
                    }`}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <div className="flex items-center justify-between">
                      <h4 className={`font-medium text-xs truncate ${
                        isActive ? 'text-blue-800' : 'text-gray-900'
                      }`}>
                        {chatTitles[chatId]}
                      </h4>
                      {isActive && (
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full ml-2 flex-shrink-0"></div>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className={`text-xs ${
                        isActive ? 'text-blue-500' : 'text-gray-500'
                      }`}>
                        {getRelativeTime(messages[messages.length - 1]?.timestamp || new Date())}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        isActive 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {messages.length}
                      </span>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Minimal Footer */}
          <div className="p-2 border-t border-gray-200/50 bg-gray-50/50">
            <div className="text-center">
              <p className="text-xs text-gray-600">
                {Object.keys(chatHistory).length} conversations
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  // Compact Family Dashboard Component
  const CompactFamilyDashboard = () => {
    const riskScore = Math.round(healthAnalytics.riskScore);
    const getRiskColor = (score) => {
      if (score <= 30) return 'text-green-600';
      if (score <= 70) return 'text-yellow-600';
      return 'text-red-600';
    };

    return (
      <motion.div 
        className="bg-gradient-to-br from-white via-purple-50/50 to-pink-50/30 p-2 rounded-2xl shadow-2xl border border-white/60 backdrop-blur-sm"
        initial={animationsCompleted ? false : { opacity: 0, x: 20 }}
        animate={animationsCompleted ? false : { opacity: 1, x: 0 }}
        transition={animationsCompleted ? false : { duration: 0.5, delay: 0.4 }}
        style={{
          boxShadow: '0 20px 40px -12px rgba(168, 85, 247, 0.25), 0 8px 16px -4px rgba(168, 85, 247, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
        }}
      >
        <div className="flex items-center space-x-2 mb-3">
          <div className="bg-gradient-to-br from-purple-500 to-pink-600 p-2 rounded-xl shadow-lg">
            <Users className="w-4 h-4 text-white" />
          </div>
          <h3 className="font-bold text-gray-900 text-sm">Family Network</h3>
        </div>
        
        <div className="space-y-3">
          {familyDashboard.connectedFamily.map((member, index) => (
            <motion.div
              key={member.name}
              className="flex items-center justify-between p-3 bg-white/60 rounded-xl shadow-md border border-white/40"
              initial={animationsCompleted ? false : { opacity: 0, y: 20 }}
              animate={animationsCompleted ? false : { opacity: 1, y: 0 }}
              transition={animationsCompleted ? false : { delay: index * 0.1 }}
              whileHover={{ scale: 1.02, boxShadow: '0 6px 12px -3px rgba(168, 85, 247, 0.2)' }}
            >
              <div className="flex items-center space-x-3">
                <motion.div 
                  className={`w-3 h-3 rounded-full ${member.status === 'online' ? 'bg-green-400' : 'bg-gray-400'}`}
                  animate={member.status === 'online' ? { scale: [1, 1.3, 1] } : {}}
                  transition={{ repeat: Infinity, duration: 2 }}
                />
                <div>
                  <h4 className="font-semibold text-sm text-gray-900">{member.name}</h4>
                  <p className="text-xs text-purple-600 font-medium">{member.relationship}</p>
                  <p className="text-xs text-gray-500">{member.role}</p>
                </div>
              </div>
              <div className="text-right">
                <motion.button
                  className="p-2 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => {
                    addMessageToCurrentChat({
                      id: Date.now(),
                      text: `Contacting ${member.name} (${member.relationship})...`,
                      sender: 'user',
                      timestamp: new Date()
                    });
                    
                    setTimeout(() => {
                      addMessageToCurrentChat({
                        id: Date.now() + 1,
                        text: `📞 I've notified ${member.name} about your request. They should receive the message shortly at ${member.phone}. ${member.status === 'online' ? 'They are currently online and should respond quickly.' : `They were last seen ${member.lastSeen}.`}`,
                        sender: 'nurse',
                        timestamp: new Date(),
                        type: 'family-contact'
                      });
                    }, 1000);
                  }}
                  title={`Contact ${member.name}`}
                >
                  <Phone className="w-4 h-4 text-purple-600" />
                </motion.button>
              </div>
            </motion.div>
          ))}
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="bg-white/60 rounded-lg p-2 text-center border border-white/40">
              <p className="text-xs text-gray-600">Risk Level</p>
              <p className={`font-bold text-sm ${getRiskColor(riskScore)}`}>{riskScore}/100</p>
            </div>
            <div className="bg-white/60 rounded-lg p-2 text-center border border-white/40">
              <p className="text-xs text-gray-600">Contacts</p>
              <p className="font-bold text-sm text-purple-600">{familyDashboard.emergencyContacts}</p>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  // Control de notificaciones únicas
  const [sentNotifications, setSentNotifications] = useState(new Set());
  const [lastNotificationTimes, setLastNotificationTimes] = useState({});

  const addNotification = (notification) => {
    const now = Date.now();
    const notificationKey = `${notification.title}-${notification.message}`;
    const lastTime = lastNotificationTimes[notificationKey] || 0;
    
    // Evitar duplicados en los últimos 5 minutos
    if (now - lastTime < 300000) {
      return;
    }

    // Determinar la categoría basada en el tipo y el ícono
    let category = 'all';
    if (notification.icon === 'pill') category = 'medication';
    else if (notification.icon === 'droplet') category = 'hydration';
    else if (notification.icon === 'activity') category = 'activity';
    else if (notification.icon === 'smile') category = 'mood';
    else if (notification.type === 'critical' || notification.priority === 'high') category = 'urgent';

    const newNotification = {
      id: now,
      timestamp: new Date(),
      category,
      ...notification
    };
    
    setNotifications(prev => [newNotification, ...prev].slice(0, 50));
    setLastNotificationTimes(prev => ({
      ...prev,
      [notificationKey]: now
    }));
  };

  // Generador de mensajes variados
  const getRandomMessage = (type) => {
    const messages = {
      hydration: [
        "Would you like some refreshing water with lemon? 🍋",
        "Time for a hydration break! How about some herbal tea? 🫖",
        "Let's boost your energy with some water! 💧",
        "Stay healthy and hydrated - I'll remind you throughout the day! 🌟",
        "Your body needs water now! Let me help you stay energized 💪",
        "Quick hydration boost? I've got your back! 🌊",
        "Water = Energy. Time to recharge! ⚡",
        "Feeling tired? Let's hydrate and revitalize! 🔋"
      ],
      activity: [
        "Shall we do some gentle stretches together? 🧘‍♂️",
        "Time for a quick walk around the house! 🚶‍♂️",
        "Let's get the blood flowing with some easy movements! 💪",
        "How about a brief exercise break? I'll guide you! 🌟",
        "3 minutes of movement can change your day! Let's go! 🎯",
        "Your body is asking for movement - I can feel it! 🌟",
        "Quick energy boost? Stand up, let's move! ⚡",
        "Movement is medicine - time for your dose! 💪"
      ],
      medication: [
        "Time for your health routine! I'm here to help you stay on track 💊",
        "Let's take care of your medication schedule together! ⏰",
        "Your well-being is important - medication time! 🌟",
        "Ready for your daily health boost? Let me assist you! 💪",
        "Quick health check: Your medication is waiting! ⚡",
        "This pill keeps you strong - let's take it now! 💪",
        "Your future self will thank you - medication time! 🎯",
        "Power up your health! Time for your meds! 🌟"
      ],
      mood: [
        "How's your day going? I'm here to chat! 😊",
        "Let's take a moment to check in on your mood 💝",
        "Share your thoughts with me - I'm all ears! 🌟",
        "Your emotional well-being matters to me! How are you feeling? 💫",
        "Quick mood boost? Let's chat for a minute! ⚡",
        "You're not alone - I'm right here with you! 💝",
        "Time for a 60-second happiness check! 🌟",
        "Let's create some joy together! Ready? 💫"
      ],
      urgent: [
        "⚠️ ALERT: Immediate attention needed!",
        "🚨 Critical Update: Action required now!",
        "⚡ URGENT: Quick response needed!",
        "🔥 Time-sensitive alert!",
        "💥 Important: Immediate action required!",
        "⏰ Don't wait - Act now!",
        "🎯 Critical moment: Take action!",
        "⚡ Quick response needed!"
      ]
    };

    const availableMessages = messages[type].filter(msg => !notificationHistory[type].includes(msg));
    
    // Si todos los mensajes fueron usados, resetear el historial
    if (availableMessages.length === 0) {
      notificationHistory[type] = [];
      return getRandomMessage(type);
    }

    const selectedMessage = availableMessages[Math.floor(Math.random() * availableMessages.length)];
    
    // Agregar al historial
    notificationHistory[type].push(selectedMessage);
    if (notificationHistory[type].length > MAX_HISTORY) {
      notificationHistory[type].shift(); // Remover el mensaje más antiguo
    }

    return selectedMessage;
  };

  const [lastCategory, setLastCategory] = useState('hydration');

  const getNextCategory = (current) => {
    const categories = ['hydration', 'activity', 'medication', 'mood'];
    const currentIndex = categories.indexOf(current);
    return categories[(currentIndex + 1) % categories.length];
  };

  const checkProactiveNotifications = () => {
    if (!notificationsEnabled) return;

    const now = new Date();
    const nextCategory = getNextCategory(lastCategory);
    setLastCategory(nextCategory);

    // Enviar notificación basada en la categoría actual
    switch(nextCategory) {
      case 'hydration':
        if (patientData.hydration) {
          const percentOfGoal = (patientData.hydration.current / patientData.hydration.goal) * 100;
          addNotification({
            type: 'info',
            title: '💧 Hydration Check',
            message: `${Math.round(percentOfGoal)}% of goal reached`,
            icon: 'droplet',
            priority: 'medium'
          });
        }
        break;

      case 'activity':
        const lastActivity = new Date(patientData.lastActivity || now);
        const inactiveHours = Math.floor((now - lastActivity) / (1000 * 60 * 60));
        addNotification({
          type: 'info',
          title: '🚀 Activity Time!',
          message: `${inactiveHours}hr since last activity`,
          icon: 'activity',
          priority: 'medium'
        });
        break;

      case 'medication':
        const nextMed = patientData.medications[Math.floor(Math.random() * patientData.medications.length)];
        addNotification({
          type: 'info',
          title: '💊 Medication Check',
          message: `Time for ${nextMed.name}`,
          icon: 'pill',
          priority: 'medium'
        });
        break;

      case 'mood':
        addNotification({
          type: 'info',
          title: '💝 Mood Check',
          message: 'How are you feeling?',
          icon: 'smile',
          priority: 'medium'
        });
        break;
    }

    // Mantener las verificaciones críticas separadas
    patientData.medications.forEach(med => {
      if (med.remaining <= 3) {
        addNotification({
          type: 'critical',
          title: 'Low Medication Alert',
          message: `${med.name}: ${med.remaining} doses left`,
          icon: 'pill',
          priority: 'high'
        });
      }

      if (med.lastTaken) {
        const lastTaken = new Date(med.lastTaken);
        const hoursSinceLastDose = Math.floor((now - lastTaken) / (1000 * 60 * 60));
        
        if (hoursSinceLastDose > med.frequencyHours + 1) {
          addNotification({
            type: 'critical',
            title: 'Missed Medication',
            message: `Take ${med.name} now`,
            icon: 'pill',
            priority: 'high'
          });
        }
      }
    });

    // Verificar hidratación
    if (patientData.hydration) {
      const lastDrink = new Date(patientData.hydration.lastDrink);
      const hoursSinceLastDrink = Math.floor((now - lastDrink) / (1000 * 60 * 60));
      const percentOfGoal = (patientData.hydration.current / patientData.hydration.goal) * 100;

      if (percentOfGoal < 40 && hoursSinceLastDrink >= 2) {
        addNotification({
          type: 'warning',
          title: '💧 Hydration Alert',
          message: `${Math.round(percentOfGoal)}% hydration - Time to drink water`,
          icon: 'droplet',
          priority: 'high'
        });
      }
    }

    // Verificar actividad física
    const lastActivity = new Date(patientData.lastActivity || now);
    const inactiveHours = Math.floor((now - lastActivity) / (1000 * 60 * 60));
    
    if (inactiveHours >= 3) {
      addNotification({
        type: 'warning',
        title: 'Activity Reminder',
        message: `${inactiveHours}hr inactive - Time to move`,
        icon: 'activity',
        priority: 'medium'
      });
    }

    // Verificar estado de ánimo
    const lastMoodCheck = new Date(patientData.lastMoodCheck || now);
    const daysSinceLastMoodCheck = Math.floor((now - lastMoodCheck) / (1000 * 60 * 60 * 24));
    
    if (daysSinceLastMoodCheck >= 2) {
      addNotification({
        type: 'info',
        title: 'Mood Check',
        message: 'How are you feeling today?',
        icon: 'smile',
        priority: 'medium'
      });
    }
  };

  // Start proactive notifications check
  useEffect(() => {
    // Ejecutar inmediatamente la primera vez
    checkProactiveNotifications();
    
    // Configurar el intervalo para cada 1.5 minutos (90000 ms)
    const interval = setInterval(checkProactiveNotifications, 90000);
    return () => clearInterval(interval);
  }, [notificationsEnabled, patientData]);

  const clearNotification = (id) => {
    // if (activeNotification?.id === id) { // Comentado para deshabilitar activeNotification
    //   setActiveNotification(null);
    // }
  };

  const toggleNotifications = () => {
    setNotificationsEnabled(!notificationsEnabled);
    // if (!notificationsEnabled) { // Comentado para deshabilitar activeNotification
    //   // Clear any existing notification when enabling
    //   setActiveNotification(null);
    // }
  };

  const clearAllNotifications = () => {
    setNotifications([]);
    // setActiveNotification(null); // Comentado para deshabilitar activeNotification
  };

  const EmergencyCountdown = () => {
  return (
      <AnimatePresence>
        {emergencyCall.countdown > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-md flex flex-col items-center justify-center z-[100] text-white"
          >
            <div className="text-center space-y-12">
              <motion.div 
                className="space-y-4"
                initial={{ y: -20 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-4xl font-bold text-red-500">[TEST MODE]</h2>
                <h3 className="text-2xl">Emergency call will start in</h3>
              </motion.div>
              
              <motion.div
                key={emergencyCall.countdown}
                initial={{ scale: 1.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div
                  className="text-[200px] font-bold"
                  style={{
                    background: "linear-gradient(to right, #ff0000, #ff6b6b)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent"
                  }}
                >
                  {emergencyCall.countdown}
                </div>
              </motion.div>

              <motion.p 
                className="text-xl text-gray-300"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                Emergency services will be notified
              </motion.p>

              <motion.button
                onClick={() => {
                  setEmergencyCall(prev => ({
                    ...prev,
                    countdown: 0,
                    calling: false,
                    hasBeenTriggered: false
                  }));
                }}
                className="bg-white text-red-500 px-8 py-3 rounded-full text-xl font-semibold hover:bg-gray-100 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Cancel Emergency
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  const formatAppointmentMessage = (appointmentDate, doctorName) => {
    const now = new Date();
    const appointment = new Date(appointmentDate);
    
    // Si la fecha es inválida o está en el pasado, no mostrar notificación
    if (isNaN(appointment.getTime()) || appointment < now) {
      return null;
    }

    const diffTime = appointment - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const appointmentTime = appointment.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const appointmentDay = appointment.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });

    // Mensajes personalizados según la proximidad de la cita
    if (diffDays === 0) {
      return {
        type: 'critical',
        title: '⚡ Today\'s Appointment',
        message: `Your appointment with Dr. ${doctorName} is TODAY at ${appointmentTime}. Please ensure you have:\n• Your medication list\n• Recent test results\n• List of any new symptoms\n• Transportation arranged`,
        icon: 'calendar',
        priority: 'high'
      };
    } else if (diffDays === 1) {
      return {
        type: 'warning',
        title: '🗓️ Tomorrow\'s Appointment',
        message: `Your appointment with Dr. ${doctorName} is tomorrow at ${appointmentTime}. Time to prepare:\n• Review your health concerns\n• Gather your medical documents\n• Confirm your transportation\n• Set your reminder`,
        icon: 'calendar',
        priority: 'high'
      };
    } else if (diffDays <= 3) {
      return {
        type: 'warning',
        title: '📅 Upcoming Appointment Soon',
        message: `Your appointment with Dr. ${doctorName} is in ${diffDays} days on ${appointmentDay}. Start preparing your health summary and questions.`,
        icon: 'calendar',
        priority: 'medium'
      };
    } else if (diffDays <= 7) {
      return {
        type: 'info',
        title: '📋 Appointment Next Week',
        message: `Your appointment with Dr. ${doctorName} is next week on ${appointmentDay}. Begin noting down any health concerns or questions you'd like to discuss.`,
        icon: 'calendar',
        priority: 'medium'
      };
    }
    
    return null;
  };

  const [isHealthAnalyticsExpanded, setIsHealthAnalyticsExpanded] = useState(false);
  const [isVitalSignsExpanded, setIsVitalSignsExpanded] = useState(false);
  const [isFamilyNetworkExpanded, setIsFamilyNetworkExpanded] = useState(false);
  const [isHealthRewardsExpanded, setIsHealthRewardsExpanded] = useState(false);
  const [isAIInsightsExpanded, setIsAIInsightsExpanded] = useState(true);
  const [isHydrationExpanded, setIsHydrationExpanded] = useState(false);
  const [isTodayProgressExpanded, setIsTodayProgressExpanded] = useState(false);
  const [isSmartDevicesExpanded, setIsSmartDevicesExpanded] = useState(false);

  // Categorías de notificaciones
  const notificationCategories = {
    all: { label: 'All', icon: <Bell className="w-4 h-4" /> },
    medication: { label: 'Medication', icon: <Pill className="w-4 h-4" /> },
    hydration: { label: 'Hydration', icon: <Droplet className="w-4 h-4" /> },
    activity: { label: 'Activity', icon: <Activity className="w-4 h-4" /> },
    mood: { label: 'Mood', icon: <Smile className="w-4 h-4" /> },
    urgent: { label: 'Urgent', icon: <AlertTriangle className="w-4 h-4" /> }
  };

  // Función para filtrar notificaciones por categoría
  const getFilteredNotifications = () => {
    if (selectedCategory === 'all') return notifications;
    return notifications.filter(n => n.category === selectedCategory);
  };

  // Función para manejar la expansión de paneles
  const handlePanelExpansion = (panelName) => {
    // Si el panel ya está expandido, lo colapsamos
    if (
      (panelName === 'aiInsights' && isAIInsightsExpanded) ||
      (panelName === 'healthAnalytics' && isHealthAnalyticsExpanded) ||
      (panelName === 'vitalSigns' && isVitalSignsExpanded) ||
      (panelName === 'familyNetwork' && isFamilyNetworkExpanded) ||
      (panelName === 'healthRewards' && isHealthRewardsExpanded) ||
      (panelName === 'hydration' && isHydrationExpanded) ||
      (panelName === 'todayProgress' && isTodayProgressExpanded) ||
      (panelName === 'smartDevices' && isSmartDevicesExpanded)
    ) {
      setIsAIInsightsExpanded(false);
      setIsHealthAnalyticsExpanded(false);
      setIsVitalSignsExpanded(false);
      setIsFamilyNetworkExpanded(false);
      setIsHealthRewardsExpanded(false);
      setIsHydrationExpanded(false);
      setIsTodayProgressExpanded(false);
      setIsSmartDevicesExpanded(false);
      return;
    }

    // Si no está expandido, colapsamos todos y expandimos el seleccionado
    setIsAIInsightsExpanded(panelName === 'aiInsights');
    setIsHealthAnalyticsExpanded(panelName === 'healthAnalytics');
    setIsVitalSignsExpanded(panelName === 'vitalSigns');
    setIsFamilyNetworkExpanded(panelName === 'familyNetwork');
    setIsHealthRewardsExpanded(panelName === 'healthRewards');
    setIsHydrationExpanded(panelName === 'hydration');
    setIsTodayProgressExpanded(panelName === 'todayProgress');
    setIsSmartDevicesExpanded(panelName === 'smartDevices');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <EmergencyCountdown />
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          className="absolute top-10 left-10 w-32 h-32 bg-blue-100/30 rounded-full blur-xl"
          animate={{ 
            scale: [1, 1.2, 1],
            x: [0, 20, 0],
            y: [0, -10, 0]
          }}
          transition={{ repeat: Infinity, duration: 8, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute top-1/3 right-20 w-24 h-24 bg-purple-100/30 rounded-full blur-xl"
          animate={{ 
            scale: [1, 1.3, 1],
            x: [0, -15, 0],
            y: [0, 15, 0]
          }}
          transition={{ repeat: Infinity, duration: 12, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-20 left-1/4 w-20 h-20 bg-green-100/30 rounded-full blur-xl"
          animate={{ 
            scale: [1, 1.1, 1],
            x: [0, 10, 0],
            y: [0, -20, 0]
          }}
          transition={{ repeat: Infinity, duration: 10, ease: "easeInOut" }}
        />
      </div>

      {/* Enhanced Celebration Animation with Blur Background */}
      <AnimatePresence>
        {celebrateAction && (
          <>
            {/* Blur Background Overlay - Clickable to close */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 backdrop-blur-md z-50 cursor-pointer"
              transition={{ duration: 0.01 }}
              onClick={() => setCelebrateAction(false)}
            />
            
            {/* Celebration Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              className="fixed inset-0 flex items-center justify-center z-50"
              transition={{ duration: 0.02 }}
              onClick={() => setCelebrateAction(false)}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.2, 1] }}
                exit={{ scale: 0 }}
                className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-4 rounded-full text-2xl font-bold shadow-2xl border-4 border-white/20"
                transition={{ duration: 0.03 }}
                onClick={(e) => e.stopPropagation()}
              >
                🎉 Great Job! 🎉
              </motion.div>
              {/* Enhanced Confetti effect */}
              {[...Array(30)].map((_, i) => (
                <motion.div
                  key={i}
                  className={`absolute w-3 h-3 rounded-full ${
                    ['bg-yellow-400', 'bg-pink-400', 'bg-blue-400', 'bg-green-400', 'bg-purple-400'][i % 5]
                  }`}
                  initial={{ 
                    x: window.innerWidth / 2,
                    y: window.innerHeight / 2,
                    scale: 0 
                  }}
                  animate={{ 
                    x: window.innerWidth / 2 + (Math.random() - 0.5) * 500,
                    y: window.innerHeight + 100,
                    scale: 1,
                    rotate: 360 * 3
                  }}
                  transition={{ 
                    duration: 0.05,
                    delay: Math.random() * 0.01,
                    ease: "easeOut"
                  }}
                />
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Compact Header */}
      <motion.header 
        className="bg-gradient-to-r from-white via-blue-50/80 to-purple-50/80 backdrop-blur-md shadow-lg border-b-2 border-blue-100/50 sticky top-0 z-40"
        initial={animationsCompleted ? false : { opacity: 0, y: -20 }}
        animate={animationsCompleted ? false : { opacity: 1, y: 0 }}
        transition={animationsCompleted ? false : { duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-3 py-3">
          <div className="flex items-center justify-between">
            {/* Left Section - Compact */}
            <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <motion.div 
                className="bg-gradient-to-br from-blue-500 to-purple-600 p-2 rounded-xl shadow-lg"
                whileHover={{ scale: 1.1, rotate: 5 }}
                animate={{ rotate: pulseNotification ? [0, 10, -10, 0] : 0 }}
                transition={{ duration: 0.5 }}
              >
                <Stethoscope className="w-5 h-5 text-white" />
              </motion.div>
              <div>
                  <div className="flex items-center space-x-4">
                <h1 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  ElderCare AI
                </h1>
                    <div className="flex items-center space-x-2">
                      <motion.div 
                        className="flex items-center space-x-1 bg-white/70 backdrop-blur-sm px-2 py-1 rounded-lg border border-blue-200"
                        whileHover={{ scale: 1.05 }}
                      >
                        <img src="https://pplx.ai/favicon.ico" alt="Perplexity" className="w-3 h-3" />
                        <span className="text-xs text-gray-600">AI</span>
                      </motion.div>
                      <motion.div 
                        className="flex items-center space-x-1 bg-white/70 backdrop-blur-sm px-2 py-1 rounded-lg border border-purple-200"
                        whileHover={{ scale: 1.05 }}
                      >
                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                        <span className="text-xs text-gray-600">Sonar</span>
                      </motion.div>
                    </div>
                  </div>
                <p className="text-xs text-gray-600 font-medium">Powered by Perplexity & Sonar</p>
                </div>
              </div>
            </div>

            {/* Center Section - Status */}
            <div className="flex flex-col items-center space-y-1">
              <motion.div 
                className="flex items-center space-x-2 bg-white/70 backdrop-blur-sm px-3 py-1.5 rounded-full border border-green-200 shadow-sm"
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ repeat: Infinity, duration: 3 }}
              >
                <motion.div 
                  className="w-2 h-2 bg-green-400 rounded-full"
                  animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                />
                <span className="text-xs font-medium text-gray-700">Monitoring {patientData.name}</span>
              </motion.div>
              
              {/* NEW: Family Status */}
              <div className="flex items-center space-x-1">
                <Users className="w-3 h-3 text-purple-600" />
                <span className="text-xs text-gray-600">Family:</span>
                {familyDashboard.connectedFamily.map((member, index) => (
                  <div key={member.name} className="flex items-center space-x-1">
                    <motion.div 
                      className={`w-1.5 h-1.5 rounded-full ${member.status === 'online' ? 'bg-green-400' : 'bg-gray-400'}`}
                      animate={member.status === 'online' ? { scale: [1, 1.3, 1] } : {}}
                      transition={{ repeat: Infinity, duration: 3 }}
                    />
                    <span className={`text-xs ${member.status === 'online' ? 'text-green-600 font-medium' : 'text-gray-500'}`} title={`${member.relationship} - ${member.role} (${member.location})`}>
                      {member.name} ({member.relationship})
                    </span>
                    {index < familyDashboard.connectedFamily.length - 1 && (
                      <span className="text-xs text-gray-400">•</span>
                    )}
                  </div>
                ))}
                <span className="text-xs text-gray-600">• Risk: {Math.round(healthAnalytics.riskScore)}/100</span>
              </div>
              

            </div>

            {/* Right Section - Emergency & History */}
            <div className="flex items-center space-x-6">
              {/* Notification Button */}
              <motion.button 
                onClick={() => setShowNotificationCenter(!showNotificationCenter)}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-medium text-white shadow-2xl border-2 border-white/20 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                whileHover={{ scale: 1.05, y: -1, boxShadow: '0 12px 24px -6px rgba(59, 130, 246, 0.5)' }}
                whileTap={{ scale: 0.95 }}
                style={{
                  boxShadow: '0 8px 16px -4px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.3)'
                }}
              >
                <Bell className={`w-4 h-4 ${notifications.length > 0 ? 'animate-bounce' : ''}`} />
                <span>Notifications</span>
                {notifications.length > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center ml-1">
                    {notifications.length}
                  </span>
                )}
              </motion.button>

              {/* Emergency Button */}
              <motion.button 
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-medium text-white shadow-2xl border-2 border-white/20 ${
                  emergencyCall.completed 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600' 
                    : 'bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600'
                }`}
                whileHover={{ scale: 1.05, y: -1, boxShadow: emergencyCall.completed ? '0 12px 24px -6px rgba(16, 185, 129, 0.5)' : '0 12px 24px -6px rgba(239, 68, 68, 0.5)' }}
                whileTap={{ scale: 0.95 }}
                onClick={handleEmergencyCall}
                style={{
                  boxShadow: emergencyCall.completed 
                    ? '0 8px 16px -4px rgba(16, 185, 129, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.3)' 
                    : '0 8px 16px -4px rgba(239, 68, 68, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.3)'
                }}
              >
                {emergencyCall.completed ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>Emergency Sent</span>
                  </>
                ) : (
                  <>
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ repeat: Infinity, duration: 1.5 }}
                    >
                      <Phone className="w-4 h-4" />
                    </motion.div>
                    <span>Emergency</span>
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Active Notification */}
      {/* <AnimatePresence>
        {activeNotification && (
            <motion.div
            initial={{ opacity: 0, y: -20, x: "100%" }}
            animate={{ opacity: 1, y: 0, x: "0%" }}
            exit={{ opacity: 0, x: "100%" }}
            className="fixed top-28 right-4 max-w-sm bg-white rounded-lg shadow-xl border border-gray-100 p-4 z-50"
          >
            <div className="flex items-start space-x-3">
              <div className={`p-2 rounded-full ${
                activeNotification.type === 'warning' ? 'bg-yellow-100 text-yellow-600' :
                activeNotification.type === 'error' ? 'bg-red-100 text-red-600' :
                'bg-blue-100 text-blue-600'
              }`}>
                {activeNotification.icon === 'pill' ? <Pill className="w-5 h-5" /> :
                 activeNotification.icon === 'calendar' ? <Calendar className="w-5 h-5" /> :
                 activeNotification.icon === 'droplet' ? <Droplet className="w-5 h-5" /> :
                 <Activity className="w-5 h-5" />}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">{activeNotification.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{activeNotification.message}</p>
                </div>
              <button
                onClick={() => clearNotification(activeNotification.id)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
              </div>
            </motion.div>
        )}
      </AnimatePresence> */}

      {/* Notification Center */}
      <AnimatePresence>
        {showNotificationCenter && (
            <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-24 w-96 max-h-[calc(100vh-8rem)] bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50"
            style={{ right: '25%', left: 'auto', top: '9%' }}
          >
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50">
              <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={clearAllNotifications}
                  className="text-gray-500 hover:text-gray-700"
                  title="Clear all notifications"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <button
                  onClick={toggleNotifications}
                  className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                    notificationsEnabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                  title={notificationsEnabled ? 'Disable notifications' : 'Enable notifications'}
                >
                  <div className={`absolute w-4 h-4 bg-white rounded-full top-1 transition-transform duration-200 ${
                    notificationsEnabled ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
                <button
                  onClick={() => setShowNotificationCenter(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex space-x-1 p-2 bg-gray-50 border-b border-gray-100">
              {Object.entries(notificationCategories).map(([key, { label, icon }]) => (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedCategory === key
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:bg-white/50'
                  }`}
                >
                  {icon}
                  <span>{label}</span>
                  {key !== 'all' && notifications.filter(n => n.category === key).length > 0 && (
                    <span className="ml-1 bg-blue-100 text-blue-600 text-xs px-1.5 py-0.5 rounded-full">
                      {notifications.filter(n => n.category === key).length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="overflow-y-auto max-h-[calc(100vh-12rem)]">
              {getFilteredNotifications().length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Bell className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>No notifications in this category</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {getFilteredNotifications().map(notification => (
                    <div key={notification.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-full ${
                          notification.type === 'warning' ? 'bg-yellow-100 text-yellow-600' :
                          notification.type === 'error' ? 'bg-red-100 text-red-600' :
                          notification.type === 'critical' ? 'bg-red-100 text-red-600' :
                          'bg-blue-100 text-blue-600'
                        }`}>
                          {notification.icon === 'pill' ? <Pill className="w-5 h-5" /> :
                           notification.icon === 'calendar' ? <Calendar className="w-5 h-5" /> :
                           notification.icon === 'droplet' ? <Droplet className="w-5 h-5" /> :
                           notification.icon === 'smile' ? <Smile className="w-5 h-5" /> :
                           notification.icon === 'alert' ? <AlertTriangle className="w-5 h-5" /> :
                           <Activity className="w-5 h-5" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-gray-900">{notification.title}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              notification.priority === 'high' ? 'bg-red-100 text-red-700' :
                              notification.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {notification.priority} priority
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(notification.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-3 py-3">
        {/* Main Content Grid - More Compact */}
        <div className="grid lg:grid-cols-11 gap-12 min-h-[calc(100vh-180px)]">
          

          {/* Main Chat Area - Redesigned */}
          <div className="lg:col-span-8 space-y-4 flex flex-col">
            <motion.div 
              className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/20 rounded-3xl shadow-2xl border-2 border-white/60 overflow-hidden backdrop-blur-sm flex flex-col relative"
              initial={animationsCompleted ? false : { opacity: 0, y: 20 }}
              animate={animationsCompleted ? false : { opacity: 1, y: 0 }}
              transition={animationsCompleted ? false : { duration: 0.5 }}
              style={{
                boxShadow: '0 25px 50px -12px rgba(99, 102, 241, 0.15), 0 0 0 1px rgba(99, 102, 241, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
              }}
            >
              {/* Enhanced Chat Header */}
              <div className="bg-gradient-to-r from-blue-600 via-blue-600 to-blue-600 px-4 py-3">
                <div className="flex items-center space-x-3">
                  <motion.div 
                    className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30"
                    animate={{ 
                      boxShadow: [
                        '0 0 20px rgba(255, 255, 255, 0.3)',
                        '0 0 30px rgba(255, 255, 255, 0.5)',
                        '0 0 20px rgba(255, 255, 255, 0.3)'
                      ]
                    }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  >
                    <Bot className="w-4 h-4 text-white" />
                  </motion.div>
                  <div className="flex-1">
                    <h2 className="text-base font-bold text-white">Sophira - AI Health Companion</h2>
                    <p className="text-blue-100 text-xs">Proactive health monitoring for chronic disease management with intelligent AI insights</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {/* New Chat Button */}
                    <motion.button 
                      className="flex items-center space-x-2 px-3 py-2 rounded-xl text-sm font-medium text-white bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30"
                      whileHover={{ scale: 1.05, y: -1 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={createNewChat}
                      title="New Chat"
                    >
                      <Plus className="w-4 h-4" />
                      <span>New</span>
                    </motion.button>
                    
                    {/* Chat History Button */}
                    <motion.button 
                      className="flex items-center space-x-2 px-3 py-2 rounded-xl text-sm font-medium text-white bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30"
                      whileHover={{ scale: 1.05, y: -1 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowChatHistory(!showChatHistory)}
                      title="Chat History"
                    >
                      <MessageSquare className="w-4 h-4" />
                      <span>History</span>
                    </motion.button>
                  </div>
                </div>
                
                {/* Agent Status Indicator */}
                <div className="mt-3">
                  <AgentStatusIndicator
                    currentAgent={currentAgent}
                    isLoading={agentLoading || isTyping}
                    error={agentError}
                    agentStats={getAgentStats()}
                    onShowDetails={() => setShowAgentDetails(true)}
                  />
                </div>
              </div>

              {/* Compact Chat Messages */}
              <div className="chat-messages-container overflow-y-auto p-4 space-y-3 bg-gradient-to-br from-slate-50 via-slate-100/90 to-slate-50 relative" style={{ height: 'calc(100vh - 400px)', minHeight: '400px' }}>
                <AnimatePresence>
                  <ChatHistoryPanel />
                </AnimatePresence>
                <AnimatePresence>
                  {messages.map((message) => (
                    message.type === 'agent-response' || message.agent ? (
                      // Use AgentResponse component for agent messages
                      <AgentResponse key={message.id} message={message} />
                    ) : (
                      // Keep existing rendering for user messages and other types
                      <motion.div
                        key={message.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`flex items-start space-x-2 max-w-xs lg:max-w-md ${message.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                          <motion.div 
                            className={`flex-shrink-0 w-7 h-7 rounded-xl flex items-center justify-center shadow-md ${
                              message.sender === 'user' 
                                ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white' 
                                : message.proactive 
                                  ? 'bg-gradient-to-br from-orange-500 to-red-500 text-white'
                                  : 'bg-gradient-to-br from-green-500 to-emerald-600 text-white'
                            }`}
                            whileHover={{ scale: 1.1, rotate: 5, boxShadow: '0 8px 16px -4px rgba(0, 0, 0, 0.3)' }}
                            transition={{ type: "spring", stiffness: 300 }}
                            style={{
                              boxShadow: message.sender === 'user' 
                                ? '0 4px 8px -2px rgba(59, 130, 246, 0.4)' 
                                : message.proactive 
                                  ? '0 4px 8px -2px rgba(251, 146, 60, 0.4)'
                                  : '0 4px 8px -2px rgba(16, 185, 129, 0.4)'
                            }}
                          >
                            {message.sender === 'user' ? <User className="w-3.5 h-3.5" /> : 
                             message.proactive ? <Bell className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                          </motion.div>
                          <motion.div 
                            className={`px-4 py-3 rounded-2xl shadow-lg backdrop-blur-sm border text-sm leading-relaxed ${
                              message.sender === 'user'
                                ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-br-md border-blue-300/50'
                                : message.proactive
                                  ? 'bg-gradient-to-br from-orange-50 to-red-50 text-orange-800 rounded-bl-md border-l-4 border-orange-500'
                                  : message.type === 'emergency'
                                    ? 'bg-gradient-to-br from-red-50 to-pink-50 text-red-800 rounded-bl-md border-l-4 border-red-500'
                                    : 'bg-gradient-to-br from-gray-50 to-white text-gray-800 rounded-bl-md border-gray-200/50'
                            }`}
                            whileHover={{ scale: 1.02, y: -1, boxShadow: '0 12px 24px -6px rgba(0, 0, 0, 0.2)' }}
                            transition={{ type: "spring", stiffness: 300 }}
                            style={{
                              boxShadow: message.sender === 'user' 
                                ? '0 6px 12px -3px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.2)' 
                                : message.proactive 
                                  ? '0 6px 12px -3px rgba(251, 146, 60, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.8)'
                                  : message.type === 'emergency'
                                    ? '0 6px 12px -3px rgba(239, 68, 68, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.8)'
                                    : '0 6px 12px -3px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.8)'
                            }}
                          >
                            {message.text && (
                              <p className={`${message.type === 'emergency' ? 'font-medium whitespace-pre-line' : ''} mb-2`}>{message.text}</p>
                            )}
                            {message.attachment && (
                              <div className={`${!message.text ? '' : 'mt-2'}`}>
                                {message.attachment.type === 'image' ? (
                                  <img 
                                    src={message.attachment.url} 
                                    alt={message.attachment.name}
                                    className="max-w-[300px] rounded-lg border border-white/20"
                                  />
                                ) : (
                                  <div className="flex items-center space-x-2 bg-white/10 rounded-lg p-2 max-w-fit">
                                    <Paperclip className="w-4 h-4" />
                                    <span className="text-sm">{message.attachment.name}</span>
                                  </div>
                                )}
                              </div>
                            )}
                            <p className={`text-xs mt-2 opacity-75 ${
                              message.sender === 'user' ? 'text-blue-100' : 
                              message.proactive ? 'text-orange-600' : 
                              message.type === 'emergency' ? 'text-red-600' :
                              'text-gray-500'
                            }`}>
                              {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              {message.proactive && <span className="ml-1">🔔</span>}
                              {message.type === 'emergency' && <span className="ml-1">🚨</span>}
                            </p>
                          </motion.div>
                        </div>
                      </motion.div>
                    )
                  ))}
                </AnimatePresence>
                
                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-start"
                  >
                    <div className="flex items-start space-x-2">
                      <div className="flex-shrink-0 w-7 h-7 bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-xl flex items-center justify-center shadow-md">
                        <Bot className="w-3.5 h-3.5" />
                      </div>
                      <div className="bg-gradient-to-br from-gray-50 to-white px-4 py-3 rounded-2xl rounded-bl-md shadow-lg border border-gray-200/50 backdrop-blur-sm">
                        <div className="flex space-x-1">
                          <motion.div 
                            className="w-1.5 h-1.5 bg-blue-400 rounded-full"
                            animate={{ y: [0, -3, 0] }}
                            transition={{ repeat: Infinity, duration: 0.6, delay: 0 }}
                          />
                          <motion.div 
                            className="w-1.5 h-1.5 bg-blue-400 rounded-full"
                            animate={{ y: [0, -3, 0] }}
                            transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }}
                          />
                          <motion.div 
                            className="w-1.5 h-1.5 bg-blue-400 rounded-full"
                            animate={{ y: [0, -3, 0] }}
                            transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }}
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Enhanced Quick Actions or Attachment Preview */}
              <div className="px-4 py-2 border-t border-blue-100/50 bg-gradient-to-r from-blue-50/50 via-white/80 to-indigo-50/50 backdrop-blur-sm">
                {showAttachmentPreview ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-gray-700 font-semibold">
                        {attachmentPreview.type === 'image' ? 'Image Preview' : 'File Preview'}
                      </p>
                    <motion.button
                        onClick={handleAttachmentCancel}
                        className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <X className="w-4 h-4" />
                      </motion.button>
                    </div>
                    
                    <div className="flex flex-col items-center space-y-3">
                      {/* Preview Area */}
                      <div className="w-48 h-48 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center bg-white">
                        {attachmentPreview.type === 'image' ? (
                          <img 
                            src={attachmentPreview.previewUrl} 
                            alt="Preview" 
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center p-2">
                            <Paperclip className="w-12 h-12 text-gray-400" />
                            <p className="text-sm text-gray-500 text-center mt-2 max-w-[200px] truncate">
                              {attachmentPreview.file?.name}
                            </p>
                          </div>
                        )}
                      </div>
                      
                    </div>
                    
                  </div>
                ) : (
                  <>
                <p className="text-xs mb-1.5 font-semibold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">Quick actions:</p>
                <div className="grid grid-cols-5 gap-3">
                  {quickActions.map((action, index) => (
                    <div
                      key={index}
                      className="p-[1.5px] rounded-md bg-gradient-to-r from-blue-500 to-purple-500"
                      style={{ minHeight: '28px' }}
                    >
                      <button
                        onClick={() => {
                          handleQuickAction(action);
                          setTimeout(() => {
                            if (messagesEndRef && messagesEndRef.current) {
                              messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                            }
                          }, 200);
                        }}
                        className="flex flex-row items-center justify-center w-full h-full gap-1 px-2 py-0.5 bg-white rounded-md text-[11px] font-semibold border-none focus:outline-none min-h-[26px]"
                        whileHover={{ scale: 1.03, y: -1 }}
                        whileTap={{ scale: 0.96 }}
                      >
                        {/* Icono con degradado */}
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <linearGradient id={`quick-gradient-${index}`} x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
                              <stop stopColor="#3B82F6" />
                              <stop offset="1" stopColor="#A21CAF" />
                            </linearGradient>
                          </defs>
                          {React.createElement(action.icon, {
                            width: 22,
                            height: 22,
                            stroke: `url(#quick-gradient-${index})`,
                            fill: 'none',
                            strokeWidth: 2,
                            style: { display: 'block', margin: '0 auto' }
                          })}
                        </svg>
                        <span className="bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent leading-tight">
                          {action.text}
                        </span>
                      </button>
                    </div>
                  ))}
                </div>
                  </>
                )}
              </div>


              {/* ChatGPT-style Message Input */}
              <div className="p-4 bg-white border-t border-gray-200">
                <div className="max-w-4xl mx-auto">
                  <div className="relative flex items-center space-x-3 bg-white border border-gray-300 rounded-3xl shadow-sm hover:shadow-md transition-shadow duration-200 p-3">
                    {/* Left side buttons */}
                    <div className="flex items-center space-x-2">
                      {/* Attachment Button */}
                      <motion.button
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = '.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png';
                          input.onchange = (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleFileSelect(file);
                            }
                          };
                          input.click();
                        }}
                        className="p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors duration-200"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title="Attach file"
                      >
                        <Paperclip className="w-5 h-5" />
                      </motion.button>

                      {/* Photo Button */}
                      <motion.button
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleImageSelect(file);
                            }
                          };
                          input.click();
                        }}
                        className="p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors duration-200"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title="Take photo"
                      >
                        <Camera className="w-5 h-5" />
                      </motion.button>
                    </div>

                    {/* Input field */}
                    <div className="flex-1 min-w-0">
                      <textarea
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        placeholder="Message ElderCare AI..."
                        className="w-full resize-none border-0 bg-transparent px-2 py-2 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-0 max-h-32"
                        rows="1"
                        style={{
                          minHeight: '24px',
                          maxHeight: '128px',
                          overflowY: 'auto'
                        }}
                        onInput={(e) => {
                          e.target.style.height = 'auto';
                          e.target.style.height = Math.min(e.target.scrollHeight, 128) + 'px';
                        }}
                      />
                    </div>

                    {/* Right side buttons */}
                    <div className="flex items-center space-x-2">
                      {/* Voice Button */}
                      <motion.button
                        onClick={startVoiceRecognition}
                        disabled={voiceRecognition.isListening}
                        className={`p-2.5 rounded-full transition-colors duration-200 ${
                          voiceRecognition.isListening 
                            ? 'text-red-500 bg-red-50 hover:bg-red-100' 
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title={voiceRecognition.isListening ? "Listening..." : "Voice input"}
                      >
                        {voiceRecognition.isListening ? (
                          <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ repeat: Infinity, duration: 0.8 }}
                          >
                            <Mic className="w-5 h-5" />
                          </motion.div>
                        ) : (
                          <Mic className="w-5 h-5" />
                        )}
                      </motion.button>

                      {/* Send Button */}
                      <motion.button
                        onClick={handleSendMessage}
                        disabled={!inputMessage.trim()}
                        className={`p-2.5 rounded-full transition-colors duration-200 ${
                          inputMessage.trim() 
                            ? 'text-white bg-blue-600 hover:bg-blue-700' 
                            : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        }`}
                        whileHover={inputMessage.trim() ? { scale: 1.1 } : {}}
                        whileTap={inputMessage.trim() ? { scale: 0.9 } : {}}
                        title="Send message"
                      >
                        <Send className="w-5 h-5" />
                      </motion.button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Interactive Components - Compact */}
            <AnimatePresence>
              {showFoodAnalyzer && <FoodAnalyzer />}
              {showVitalsInput && <VitalsInput />}
              {showMoodTracker && <MoodTracker />}
              {showBottomPanel && <BottomPanel />}
            </AnimatePresence>

            {/* Chat History Panel */}

          </div>

          {/* Left Sidebar - Patient Info Compact */}
          <div className="lg:col-span-3 space-y-3 flex flex-col h-full">
            {/* Patient Profile - Enhanced */}
            <motion.div 
              className="bg-gradient-to-br from-white via-blue-50/50 to-indigo-50/30 p-2 rounded-2xl border border-white/60 backdrop-blur-sm"
              initial={animationsCompleted ? false : { opacity: 0, x: 20 }}
              animate={animationsCompleted ? false : { opacity: 1, x: 0 }}
              transition={animationsCompleted ? false : { duration: 0.5, delay: 0.2 }}
              style={{
                boxShadow: '0 20px 40px -12px rgba(130, 130, 130, 0.25), 0 8px 16px -4px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-1">
                  <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-2 rounded-xl shadow-lg">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm">Patient Profile</h3>
                </div>
                <div className="flex items-center space-x-1">
                  <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                    Active
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {/* Basic Info */}
                <div className="space-y-2">
                  <div className="text-sm p-1 bg-white/60 rounded-lg shadow-md">
                    <span className="text-gray-600 block mb-1">Name</span>
                    <span className="font-bold text-gray-900">{patientData.name}</span>
                  </div>
                  <div className="text-sm p-1 bg-white/60 rounded-lg shadow-md">
                    <span className="text-gray-600 block mb-1">Age</span>
                    <span className="font-bold text-blue-600">{patientData.age} years</span>
                  </div>
                </div>

                {/* Health Status */}
                <div className="space-y-2">
                  <div className="text-sm p-1 bg-white/60 rounded-lg shadow-md">
                    <span className="text-gray-600 block mb-1">Risk Level</span>
                    <span className={`font-bold ${
                      healthAnalytics.riskScore <= 30 ? 'text-green-600' : 
                      healthAnalytics.riskScore <= 70 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {healthAnalytics.riskScore <= 30 ? 'Low' : 
                       healthAnalytics.riskScore <= 70 ? 'Moderate' : 'High'}
                    </span>
                  </div>
                  <div className="text-sm p-1 bg-white/60 rounded-lg shadow-md">
                    <span className="text-gray-600 block mb-1">Last Check</span>
                    <span className="font-bold text-indigo-600">Today</span>
                  </div>
                </div>

                {/* Conditions & Allergies */}
                <div className="col-span-2 text-sm p-1 bg-white/60 rounded-lg shadow-md">
                  <span className="text-gray-600 block mb-1">Conditions</span>
                  <div className="flex flex-wrap gap-1">
                    {patientData.conditions.map((condition, index) => (
                      <span key={index} className="inline-block bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs font-medium">
                        {condition}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="col-span-2 grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-white/60 rounded-lg p-2 text-center">
                    <span className="text-gray-600 block">Med Streak</span>
                    <span className="font-bold text-blue-600">{gamification.streaks.medication} days</span>
                  </div>
                  <div className="bg-white/60 rounded-lg p-2 text-center">
                    <span className="text-gray-600 block">Hydration</span>
                    <span className="font-bold text-cyan-600">{patientData.hydration.current}/{patientData.hydration.goal}</span>
                  </div>
                  <div className="bg-white/60 rounded-lg p-2 text-center">
                    <span className="text-gray-600 block">Points</span>
                    <span className="font-bold text-purple-600">{gamification.points}</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* AI Insights Panel */}
                  <motion.div
              className="bg-gradient-to-b from-purple-50 via-indigo-50 to-blue-50 p-2 rounded-2xl border border-white/60 backdrop-blur-sm"
              initial={animationsCompleted ? false : { opacity: 0, x: 20 }}
              animate={animationsCompleted ? false : { opacity: 1, x: 0 }}
              transition={animationsCompleted ? false : { duration: 0.5, delay: 0.6 }}
                    style={{
                boxShadow: '0 20px 40px -12px rgba(130, 130, 130, 0.25), 0 8px 16px -4px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
              }}
            >
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => handlePanelExpansion('aiInsights')}
              >
                      <div className="flex items-center space-x-2">
                  <div className="bg-gradient-to-b from-blue-500 to-purple-600 p-2 rounded-xl shadow-lg">
                    <Brain className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm">AI Insights</h3>
                </div>
                        <motion.div 
                  animate={{ rotate: isAIInsightsExpanded ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                        >
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                        </motion.div>
                        </div>

              <AnimatePresence>
                {isAIInsightsExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="space-y-2 mt-4">
                      {smartFeatures.aiInsights.map((insight, index) => (
                        <motion.div
                          key={index}
                          className={`p-2 rounded-lg border-l-4 shadow-md ${
                            insight.type === 'positive' ? 'border-blue-500 bg-blue-50' :
                            insight.type === 'warning' ? 'border-indigo-500 bg-indigo-50' :
                            insight.type === 'achievement' ? 'border-purple-500 bg-purple-50' :
                            'border-blue-500 bg-blue-50'
                          }`}
                          initial={animationsCompleted ? false : { opacity: 0, x: 20 }}
                          animate={animationsCompleted ? false : { opacity: 1, x: 0 }}
                          transition={animationsCompleted ? false : { delay: index * 0.2 }}
                          whileHover={{ scale: 1.02, boxShadow: '0 4px 8px -2px rgba(0, 0, 0, 0.1)' }}
                        >
                          <p className={`text-xs font-medium ${
                            insight.type === 'positive' ? 'text-blue-800' :
                            insight.type === 'warning' ? 'text-indigo-800' :
                            insight.type === 'achievement' ? 'text-purple-800' :
                            'text-blue-800'
                          }`}>
                            {insight.message}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              insight.priority === 'high' ? 'bg-blue-100 text-blue-700' :
                              insight.priority === 'medium' ? 'bg-indigo-100 text-indigo-700' :
                              'bg-purple-100 text-purple-700'
                            }`}>
                              {insight.priority} priority
                            </span>
                            <span className="text-xs text-gray-500">AI Powered</span>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            

    

            
            



             
            
            

            

            
            {/* NEW: Health Analytics Dashboard */}
            <motion.div
              className="bg-gradient-to-b from-blue-50 via-indigo-50 to-purple-50 p-2 rounded-2xl border border-white/60 backdrop-blur-sm"
              initial={animationsCompleted ? false : { opacity: 0, x: -20 }}
              animate={animationsCompleted ? false : { opacity: 1, x: 0 }}
              transition={animationsCompleted ? false : { duration: 0.5, delay: 0.3 }}
              style={{
                boxShadow: '0 20px 40px -12px rgba(130, 130, 130, 0.25), 0 8px 16px -4px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
              }}
            >
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => handlePanelExpansion('healthAnalytics')}
              >
                <h4 className="font-bold text-gray-900 text-sm flex items-center">
                  <div className="bg-gradient-to-b from-blue-500 to-indigo-600 p-1.5 rounded-lg mr-2 shadow-lg">
                    <Activity className="w-4 h-4 text-white" />
                        </div>
                  Today's Progress
                </h4>
                <motion.div
                  animate={{ rotate: isHealthAnalyticsExpanded ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                </motion.div>
              </div>

              <AnimatePresence>
                {isHealthAnalyticsExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    {/* Risk Score */}
                    <div className="mb-3 mt-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-600">Risk Score</span>
                        <span className={`text-sm font-bold ${
                          healthAnalytics.riskScore <= 30 ? 'text-green-600' : 
                          healthAnalytics.riskScore <= 70 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {Math.round(healthAnalytics.riskScore)}/100
                        </span>
                      </div>
                      <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                        <motion.div
                          className={`h-full ${
                            healthAnalytics.riskScore <= 30 ? 'bg-gradient-to-r from-green-400 to-green-600' : 
                            healthAnalytics.riskScore <= 70 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' : 
                            'bg-gradient-to-r from-red-400 to-red-600'
                          }`}
                          initial={{ width: 0 }}
                          animate={{ width: `${healthAnalytics.riskScore}%` }}
                          transition={{ duration: 1.5, ease: "easeOut" }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {healthAnalytics.riskScore <= 30 ? '✅ Low Risk - Excellent health!' : 
                         healthAnalytics.riskScore <= 70 ? '⚠️ Moderate Risk - Keep monitoring' : 
                         '🚨 High Risk - Contact doctor'}
                      </p>
                    </div>

                    {/* Health Trends */}
                    <div className="space-y-2">
                      {Object.entries(healthAnalytics.trends).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between text-sm p-2 bg-white/60 rounded-lg shadow-md">
                          <span className="text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                          <div className="flex items-center space-x-1">
                            <span className={`font-medium ${
                              value === 'excellent' || value === 'improving' || value === 'positive' ? 'text-green-600' :
                              value === 'good' || value === 'stable' ? 'text-blue-600' :
                              'text-yellow-600'
                            }`}>
                              {value}
                            </span>
                            {value === 'excellent' || value === 'improving' || value === 'positive' ? '📈' :
                             value === 'good' || value === 'stable' ? '📊' : '📉'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* NEW: Gamification Panel */}
            <motion.div
              className="bg-gradient-to-b from-blue-50 via-indigo-50 to-purple-50 p-2 rounded-2xl border border-white/60 backdrop-blur-sm"
              initial={animationsCompleted ? false : { opacity: 0, x: -20 }}
              animate={animationsCompleted ? false : { opacity: 1, x: 0 }}
              transition={animationsCompleted ? false : { duration: 0.5, delay: 0.5 }}
              style={{
                boxShadow: '0 20px 40px -12px rgba(130, 130, 130, 0.25), 0 8px 16px -4px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
              }}
            >
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => handlePanelExpansion('healthRewards')}
              >
                <h4 className="font-bold text-gray-900 text-sm flex items-center">
                  <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-1.5 rounded-lg mr-2 shadow-lg">
                    <Star className="w-4 h-4 text-white" />
                    </div>
                  Health Rewards
                </h4>
                <motion.div
                  animate={{ rotate: isHealthRewardsExpanded ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                  </motion.div>
              </div>

              <AnimatePresence>
                {isHealthRewardsExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    {/* Points and Level */}
                    <div className="flex items-center justify-between mb-3 p-2 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg shadow-md mt-4">
                      <div>
                        <p className="text-sm font-bold text-blue-600">Level {gamification.level}</p>
                        <p className="text-xs text-gray-600">{gamification.points} points</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-indigo-600">+{gamification.todayPoints}</p>
                        <p className="text-xs text-gray-600">today</p>
                      </div>
                    </div>

                    {/* Current Streaks */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs p-1.5 bg-blue-50 rounded-lg">
                        <span className="text-gray-600">💊 Medication Streak</span>
                        <span className="font-bold text-blue-600">{gamification.streaks.medication} days</span>
                      </div>
                      <div className="flex items-center justify-between text-xs p-1.5 bg-indigo-50 rounded-lg">
                        <span className="text-gray-600">💧 Hydration Streak</span>
                        <span className="font-bold text-indigo-600">{gamification.streaks.hydration} days</span>
                      </div>
                      <div className="flex items-center justify-between text-xs p-1.5 bg-purple-50 rounded-lg">
                        <span className="text-gray-600">📊 Vitals Streak</span>
                        <span className="font-bold text-purple-600">{gamification.streaks.vitals} days</span>
                      </div>
                    </div>

                    {/* Weekly Challenge */}
                    <div className="mt-3 p-2 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold text-blue-700">{gamification.weeklyChallenge.name}</span>
                        <span className="text-xs text-indigo-600">
                          {gamification.weeklyChallenge.progress}/{gamification.weeklyChallenge.target}
                        </span>
                      </div>
                      <div className="relative h-1.5 bg-blue-200 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-gradient-to-r from-blue-400 to-purple-500"
                          initial={{ width: 0 }}
                          animate={{ width: `${(gamification.weeklyChallenge.progress / gamification.weeklyChallenge.target) * 100}%` }}
                          transition={{ duration: 1, ease: "easeOut" }}
                        />
                      </div>
                      <p className="text-xs text-indigo-600 mt-1">Reward: +{gamification.weeklyChallenge.reward} points</p>
              </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Vital Signs - Compact */}
            <motion.div 
              className="bg-gradient-to-br from-white via-blue-50/50 to-indigo-50/30 p-2 rounded-2xl border border-white/60 backdrop-blur-sm"
              initial={animationsCompleted ? false : { opacity: 0, x: 20 }}
              animate={animationsCompleted ? false : { opacity: 1, x: 0 }}
              transition={animationsCompleted ? false : { duration: 0.5, delay: 0.4 }}
              style={{
                boxShadow: '0 20px 40px -12px rgba(130, 130, 130, 0.25), 0 8px 16px -4px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
              }}
            >
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => handlePanelExpansion('vitalSigns')}
              >
                <div className="flex items-center space-x-2">
                <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-2 rounded-xl shadow-lg">
                  <Activity className="w-4 h-4 text-white" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm">Vital Signs</h3>
              </div>
                <motion.div
                  animate={{ rotate: isVitalSignsExpanded ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                </motion.div>
              </div>

              <AnimatePresence>
                {isVitalSignsExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="grid grid-cols-2 gap-2 mt-4">
                {[
                  { icon: Thermometer, label: "Temp", value: patientData.vitals.temperature, color: "text-red-500", bg: "from-red-50 to-pink-50", shadow: "rgba(239, 68, 68, 0.3)" },
                  { icon: Heart, label: "HR", value: patientData.vitals.heartRate, color: "text-red-500", bg: "from-red-50 to-pink-50", shadow: "rgba(239, 68, 68, 0.3)" },
                  { icon: Scale, label: "Weight", value: patientData.vitals.weight, color: "text-blue-500", bg: "from-blue-50 to-indigo-50", shadow: "rgba(59, 130, 246, 0.3)" },
                  { icon: Activity, label: "BP", value: patientData.vitals.bloodPressure, color: "text-green-500", bg: "from-green-50 to-emerald-50", shadow: "rgba(16, 185, 129, 0.3)" }
                ].map((vital, index) => (
                  <motion.div 
                    key={vital.label}
                    className={`text-center p-2 bg-gradient-to-br ${vital.bg} rounded-xl border border-white/50 shadow-lg`}
                    whileHover={{ scale: 1.05, boxShadow: `0 8px 16px -4px ${vital.shadow}` }}
                    initial={animationsCompleted ? false : { opacity: 0, scale: 0.8 }}
                    animate={animationsCompleted ? false : { opacity: 1, scale: 1 }}
                    transition={animationsCompleted ? false : { delay: index * 0.1 }}
                    style={{
                      boxShadow: `0 4px 8px -2px ${vital.shadow}, inset 0 1px 2px rgba(255, 255, 255, 0.8)`
                    }}
                  >
                    <vital.icon className={`w-4 h-4 ${vital.color} mx-auto mb-1`} />
                    <p className="text-xs text-gray-600 font-medium">{vital.label}</p>
                    <p className="text-xs font-bold">{vital.value}</p>
                  </motion.div>
                ))}
              </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

           

            {/* Care Team - Compact */}
            <motion.div 
              className="bg-gradient-to-br from-white via-purple-50/50 to-pink-50/30 p-2 rounded-2xl border border-white/60 backdrop-blur-sm"
              initial={animationsCompleted ? false : { opacity: 0, x: 20 }}
              animate={animationsCompleted ? false : { opacity: 1, x: 0 }}
              transition={animationsCompleted ? false : { duration: 0.5, delay: 0.4 }}
              style={{
                boxShadow: '0 20px 40px -12px rgba(130, 130, 130, 0.25), 0 8px 16px -4px rgba(168, 85, 247, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
              }}
            >
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => handlePanelExpansion('familyNetwork')}
              >
                <div className="flex items-center space-x-2">
                  <div className="bg-gradient-to-br from-purple-500 to-pink-600 p-2 rounded-xl shadow-lg">
                    <Users className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm text-sm">Family Network</h3>
                </div>
                <motion.div
                  animate={{ rotate: isFamilyNetworkExpanded ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                </motion.div>
              </div>

              <AnimatePresence>
                {isFamilyNetworkExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="space-y-3 mt-4">
                      {familyDashboard.connectedFamily.map((member, index) => (
                        <motion.div
                          key={member.name}
                          className="flex items-center justify-between p-3 bg-white/60 rounded-xl shadow-md border border-white/40"
                          initial={animationsCompleted ? false : { opacity: 0, y: 20 }}
                          animate={animationsCompleted ? false : { opacity: 1, y: 0 }}
                          transition={animationsCompleted ? false : { delay: index * 0.1 }}
                          whileHover={{ scale: 1.02, boxShadow: '0 6px 12px -3px rgba(168, 85, 247, 0.2)' }}
                        >
                          <div className="flex items-center space-x-3">
                            <motion.div 
                              className={`w-3 h-3 rounded-full ${member.status === 'online' ? 'bg-green-400' : 'bg-gray-400'}`}
                              animate={member.status === 'online' ? { scale: [1, 1.3, 1] } : {}}
                              transition={{ repeat: Infinity, duration: 2 }}
                            />
                            <div>
                              <h4 className="font-semibold text-sm text-gray-900">{member.name}</h4>
                              <p className="text-xs text-purple-600 font-medium">{member.relationship}</p>
                              <p className="text-xs text-gray-500">{member.role}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <motion.button
                              className="p-2 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => {
                                addMessageToCurrentChat({
                                  id: Date.now(),
                                  text: `Contacting ${member.name} (${member.relationship})...`,
                                  sender: 'user',
                                  timestamp: new Date()
                                });
                                
                                setTimeout(() => {
                                  addMessageToCurrentChat({
                                    id: Date.now() + 1,
                                    text: `📞 I've notified ${member.name} about your request. They should receive the message shortly at ${member.phone}. ${member.status === 'online' ? 'They are currently online and should respond quickly.' : `They were last seen ${member.lastSeen}.`}`,
                                    sender: 'nurse',
                                    timestamp: new Date(),
                                    type: 'family-contact'
                                  });
                                }, 1000);
                              }}
                              title={`Contact ${member.name}`}
                            >
                              <Phone className="w-4 h-4 text-purple-600" />
                            </motion.button>
                          </div>
                        </motion.div>
                      ))}
                      
                      {/* Quick Stats */}
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <div className="bg-white/60 rounded-lg p-2 text-center border border-white/40">
                          <p className="text-xs text-gray-600">Risk Level</p>
                          <p className={`font-bold text-sm ${
                            healthAnalytics.riskScore <= 30 ? 'text-green-600' : 
                            healthAnalytics.riskScore <= 70 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {Math.round(healthAnalytics.riskScore)}/100
                          </p>
                        </div>
                        <div className="bg-white/60 rounded-lg p-2 text-center border border-white/40">
                          <p className="text-xs text-gray-600">Contacts</p>
                          <p className="font-bold text-sm text-purple-600">{familyDashboard.emergencyContacts}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            

            {/* NEW: Smart Devices Panel */}
            <motion.div 
              className="bg-gradient-to-br from-white via-purple-50/50 to-pink-50/30 p-2 rounded-2xl border border-white/60 backdrop-blur-sm"
              initial={animationsCompleted ? false : { opacity: 0, x: 20 }}
              animate={animationsCompleted ? false : { opacity: 1, x: 0 }}
              transition={animationsCompleted ? false : { duration: 0.5, delay: 0.6 }}
              style={{
                boxShadow: '0 20px 40px -12px rgba(130, 130, 130, 0.25), 0 8px 16px -4px rgba(6, 182, 212, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
              }}
            >
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => handlePanelExpansion('smartDevices')}
              >
                <div className="font-bold text-gray-900 text-sm flex items-center space-x-2">
                  <div className="bg-gradient-to-br from-cyan-500 to-blue-600 p-1.5 rounded-lg mr-2 shadow-lg">
                  <Bluetooth className="w-4 h-4 text-white" />
                </div>
                  Devices
                  </div>
                <div className="flex items-center space-x-2">
                  <motion.div
                    animate={{ rotate: isSmartDevicesExpanded ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  </motion.div>
                </div>
              </div>
              
              <AnimatePresence>
                {isSmartDevicesExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="space-y-2 mt-4">
                {Object.entries(smartFeatures.deviceConnections).map(([device, data]) => (
                  <motion.div
                    key={device}
                    className={`p-2 rounded-lg border transition-all duration-300 shadow-md ${
                      data.connected 
                        ? 'border-green-300 bg-gradient-to-r from-green-50 to-emerald-50' 
                        : 'border-gray-300 bg-gradient-to-r from-gray-50 to-gray-100'
                    }`}
                    whileHover={{ scale: 1.02, boxShadow: '0 6px 12px -3px rgba(0, 0, 0, 0.1)' }}
                    initial={animationsCompleted ? false : { opacity: 0, scale: 0.9 }}
                    animate={animationsCompleted ? false : { opacity: 1, scale: 1 }}
                    transition={animationsCompleted ? false : { delay: Object.keys(smartFeatures.deviceConnections).indexOf(device) * 0.1 }}
                    style={{
                      boxShadow: data.connected 
                        ? '0 2px 4px -1px rgba(16, 185, 129, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.8)' 
                        : '0 2px 4px -1px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.8)'
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <motion.div 
                          className={`w-3 h-3 rounded-full ${data.connected ? 'bg-green-400' : 'bg-gray-400'}`}
                          animate={data.connected ? { scale: [1, 1, 1] } : {}}
                          transition={{ repeat: Infinity, duration: 2 }}
                        />
                        <div>
                          <h4 className={`text-xs font-semibold capitalize ${data.connected ? 'text-gray-900' : 'text-gray-600'}`}>
                            {device.replace(/([A-Z])/g, ' $1')}
                          </h4>
                          {data.connected && data.lastReading && (
                            <p className="text-xs text-green-700 font-medium">{data.lastReading}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {data.connected ? (
                          <div>
                            <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                              <Wifi className="w-2 h-2 text-white" />
                            </div>
                            {data.time && <p className="text-xs text-gray-500 mt-0.5">{data.time}</p>}
                          </div>
                        ) : (
                          <motion.button
                            className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              // Simulate connecting device
                              setSmartFeatures(prev => ({
                                ...prev,
                                deviceConnections: {
                                  ...prev.deviceConnections,
                                  [device]: {
                                    ...prev.deviceConnections[device],
                                    connected: true,
                                    lastReading: device === 'smartwatch' ? '72 bpm' : '160 lbs',
                                    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                  }
                                }
                              }));
                              
                              addProactiveMessage(`✅ ${device.charAt(0).toUpperCase() + device.slice(1)} connected successfully! Now receiving real-time health data.`, 'device');
                            }}
                          >
                            Connect
                          </motion.button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            )}
              </AnimatePresence>
            </motion.div>

            {/* Hydration Tracker - Enhanced */}
            <motion.div 
              className="bg-gradient-to-br from-white via-cyan-50/50 to-blue-50/30 p-2 rounded-2xl border border-white/60 backdrop-blur-sm"
              initial={animationsCompleted ? false : { opacity: 0, x: 20 }}
              animate={animationsCompleted ? false : { opacity: 1, x: 0 }}
              transition={animationsCompleted ? false : { duration: 0.5, delay: 0.6 }}
              style={{
                boxShadow: '0 20px 40px -12px rgba(130, 130, 130, 0.25), 0 8px 16px -4px rgba(6, 182, 212, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
              }}
            >
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => handlePanelExpansion('hydration')}
              >
                <div className="flex items-center space-x-2">
                  <div className="bg-gradient-to-br from-cyan-400 to-blue-500 p-2 rounded-xl shadow-lg">
                    <Droplet className="w-4 h-4 text-white" />
                </div>
                  <h3 className="font-bold text-gray-900 text-sm">Hydration Tracker</h3>
                </div>
                <motion.div
                  animate={{ rotate: isHydrationExpanded ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                </motion.div>
              </div>
              
              <AnimatePresence>
                {isHydrationExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4">
                      {/* Water Glasses Visual */}
                      <div className="grid grid-cols-4 gap-2 mb-3">
                        {[...Array(patientData.hydration.goal)].map((_, i) => (
                          <motion.div
                            key={i}
                            className={`aspect-square rounded-xl border-2 flex items-center justify-center shadow-lg ${
                              i < patientData.hydration.current 
                                ? 'bg-gradient-to-br from-cyan-400 to-blue-500 border-cyan-300 text-white' 
                                : 'bg-gray-100 border-gray-300 text-gray-400'
                            }`}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: i * 0.1 }}
                            whileHover={{ scale: 1.1, boxShadow: '0 8px 16px -4px rgba(6, 182, 212, 0.4)' }}
                            style={{
                              boxShadow: i < patientData.hydration.current 
                                ? '0 4px 8px -2px rgba(6, 182, 212, 0.3)' 
                                : '0 2px 4px -1px rgba(0, 0, 0, 0.1)'
                            }}
                          >
                            <span className="text-xs">💧</span>
                  </motion.div>
                ))}
                      </div>

                      {/* Progress Bar */}
                      <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
                        <motion.div
                          className="absolute top-0 left-0 h-full bg-gradient-to-r from-cyan-400 to-blue-500"
                          initial={{ width: 0 }}
                          animate={{ width: `${(patientData.hydration.current / patientData.hydration.goal) * 100}%` }}
                          transition={{ duration: 1, ease: "easeOut" }}
                        />
                      </div>

                      {/* Stats */}
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">
                          {patientData.hydration.current} / {patientData.hydration.goal} glasses
                        </span>
                        <span className="text-cyan-600 font-medium">
                          {Math.round((patientData.hydration.current / patientData.hydration.goal) * 100)}%
                        </span>
                      </div>

                      {/* Quick Action Button */}
                      <motion.button
                        className="mt-3 w-full bg-gradient-to-r from-cyan-400 to-blue-500 text-white py-2 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center space-x-2"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleDrinkWater}
                      >
                        <Droplet className="w-4 h-4" />
                        <span>Add Water</span>
                      </motion.button>
              </div>
            </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            

          </div>
              
        </div>
      </div>

      {/* Compact Footer */}
      <motion.footer 
        className="mt-8 bg-gradient-to-r from-white via-blue-50/80 to-purple-50/80 backdrop-blur-md border-t-2 border-blue-100/50"
        initial={animationsCompleted ? false : { opacity: 0 }}
        animate={animationsCompleted ? false : { opacity: 1 }}
        transition={animationsCompleted ? false : { duration: 0.5, delay: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-3 py-4">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-4 mb-2">
              <motion.div 
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.05 }}
              >
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-1.5 rounded-lg">
                  <Stethoscope className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  ElderCare AI
                </span>
              </motion.div>
              
              <div className="flex items-center space-x-3">
                <motion.div 
                  className="flex items-center space-x-1 bg-white/70 backdrop-blur-sm px-2 py-1 rounded-lg border border-blue-200"
                  whileHover={{ scale: 1.05 }}
                >
                  <img src="https://pplx.ai/favicon.ico" alt="Perplexity" className="w-3 h-3" />
                  <span className="text-xs text-gray-600 font-medium">Perplexity AI</span>
                </motion.div>
                <motion.div 
                  className="flex items-center space-x-1 bg-white/70 backdrop-blur-sm px-2 py-1 rounded-lg border border-purple-200"
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span className="text-xs text-gray-600 font-medium">Sonar Tech</span>
                </motion.div>
              </div>
            </div>
            
            <p className="text-xs text-gray-600 leading-relaxed">
              <strong>Proactive health monitoring</strong> for chronic disease management with intelligent AI insights
            </p>
            
            <div className="flex items-center justify-center space-x-4 mt-2 text-xs text-gray-500">
              <span>🏥 Healthcare AI</span>
              <span>•</span>
              <span>👥 Family Connected</span>
              <span>•</span>
              <span>🚨 Emergency Ready</span>
            </div>
          </div>
        </div>
      </motion.footer>

      {/* Emergency Modal */}
      <AnimatePresence>
        {showEmergencyModal && <EmergencyModal />}
      </AnimatePresence>

      {/* Agent Details Modal */}
      <AgentDetailsModal
        isOpen={showAgentDetails}
        onClose={() => setShowAgentDetails(false)}
        currentAgent={currentAgent}
        agentStats={getAgentStats()}
        agentHistory={[]}
        availableAgents={getAvailableAgents()}
      />
      
      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-t border-gray-200 py-2 px-4 text-center text-sm text-gray-600">
        <p>
          Developed by{' '}
          <a 
            href="https://www.linkedin.com/in/sergioyupanquigomez/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Sergio Yupanqui
          </a>
          {' '}•{' '}
          <a 
            href="https://github.com/SergioYupanqui" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-purple-600 hover:text-purple-800 font-medium"
          >
            GitHub
          </a>
        </p>
      </footer>
    </div>
  );
};

export default ElderlyNurseAgent;
